import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Card, Row, Col, Select, DatePicker,
    Table, Tag, Spin, Button, Progress, Alert, Empty, Divider, Tooltip, Popover
} from 'antd';
import {
    ReloadOutlined, TrophyOutlined, CalendarOutlined, StarFilled,
    RiseOutlined, FallOutlined, MinusOutlined, ApartmentOutlined,
    BarChartOutlined, PieChartOutlined, LineChartOutlined,
    TableOutlined, FilterOutlined, WarningFilled, CheckCircleFilled,
    FilePdfOutlined, FileImageOutlined, LoadingOutlined
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import QoCService from '../../services/QoCService';
import util from '../../utils/util';
import { FacilityReportModal } from './QoCFacilityReport';
import FacilityService from '../../services/FacilityService';
import moment from 'moment';
import { trailPageView, trailReportGenerate, trailReportExport, TRAIL_MODULES } from '../../utils/activityTrail';
const { RangePicker } = DatePicker;
const { Option, OptGroup } = Select;

const CLAUSES = [
    { code:'AACC', title:'Access, Assessment and Continuity of Care', color:'#0ea5e9' },
    { code:'COP',  title:'Care of the Patient',                        color:'#8b5cf6' },
    { code:'MOM',  title:'Management of Medication',                   color:'#f59e0b' },
    { code:'PRPE', title:"Patient Rights and Patient Education",        color:'#10b981' },
    { code:'IPCM', title:'Infection Prevention and Control Mgmt',      color:'#ef4444' },
    { code:'PSQI', title:'Patient Safety and Quality Improvement',     color:'#3b82f6' },
    { code:'GROM', title:'Governance, Roles and Responsibilities',     color:'#6366f1' },
    { code:'FMS',  title:'Facility Management and Safety',             color:'#ec4899' },
    { code:'HRE',  title:'Human Resource and Education',               color:'#14b8a6' },
    { code:'ISM',  title:'Information System Management',              color:'#a855f7' },
];
const LEVELS   = ['Clinic without Maternity','Clinic with Maternity','Public Health Unit','Regional Hospital','National Referral Hospital'];
const REGIONS  = ['Hhohho','Manzini','Lubombo','Shiselweni'];
const RATING = [
    { min:98,  max:100,  label:'Outstanding',          stars:5, color:'#389e0d' },
    { min:95,  max:97.9, label:'Excellent',            stars:4, color:'#1890ff' },
    { min:90,  max:94.9, label:'Good',                 stars:3, color:'#52c41a' },
    { min:85,  max:89.9, label:'Fair',                 stars:2, color:'#faad14' },
    { min:70,  max:84.9, label:'Requires Improvement', stars:1, color:'#fa8c16' },
    { min:0,   max:69.9, label:'Inadequate',           stars:0, color:'#f5222d' },
];
const getScoringMode = () => localStorage.getItem('qoc_scoring_mode') || 'benchmark';
const getFacilityScore = (f, mode) => { const m = mode || getScoringMode(); return parseFloat((m === 'benchmark' ? (f.benchmark_percentage || f.average_percentage) : f.average_percentage) || 0); };
const SCHED_STATUS = {
    scheduled:   { color:'#1890ff', label:'Scheduled',   bg:'#e6f4ff' },
    in_progress: { color:'#faad14', label:'In Progress', bg:'#fffbe6' },
    completed:   { color:'#52c41a', label:'Completed',   bg:'#f6ffed' },
    overdue:     { color:'#f5222d', label:'Overdue',     bg:'#fff2f0' },
    cancelled:   { color:'#bbb',    label:'Cancelled',   bg:'#f5f5f5' },
};
const getRating = s => RATING.find(r => s >= r.min && s <= r.max) || RATING[RATING.length-1];

function Stars({ count, size=12 }) {
    return <span>{[1,2,3,4,5].map(i=>(
        <StarFilled key={i} style={{color: i<=count?'#faad14':'#e8e8e8', fontSize:size}}/>
    ))}</span>;
}

function KpiCard({ label, value, suffix='', color='#1890ff', icon, sub }) {
    return (
        <div style={{background:`${color}09`,border:`1px solid ${color}28`,borderRadius:10,
            padding:'14px 18px',height:'100%',position:'relative',overflow:'hidden'}}>
            <div style={{position:'absolute',right:10,top:10,opacity:0.1,fontSize:40}}>{icon}</div>
            <div style={{fontSize:10,color:'#888',fontWeight:700,textTransform:'uppercase',
                letterSpacing:0.8,marginBottom:4}}>{label}</div>
            <div style={{fontSize:26,fontWeight:900,color,lineHeight:1}}>
                {value}<span style={{fontSize:13,marginLeft:3}}>{suffix}</span>
            </div>
            {sub && <div style={{marginTop:4,fontSize:10,color:'#888'}}>{sub}</div>}
        </div>
    );
}

function SectionTitle({ id, icon, title, subtitle, color='#1890ff', children }) {
    const storageKey = `exec_dash_collapsed_${id}`;
    const [collapsed, setCollapsed] = React.useState(() => {
        if (id === 'sec-overview') return false;
        try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
    });
    const toggle = () => {
        const next = !collapsed;
        setCollapsed(next);
        try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch {}
    };
    return (
        <div style={{margin:'24px 0 0'}}>
            <div id={id} onClick={toggle}
                style={{display:'flex',alignItems:'center',gap:10,
                    padding:'10px 16px',cursor:'pointer',userSelect:'none',
                    background:`linear-gradient(135deg,${color}12,${color}05)`,
                    borderLeft:`5px solid ${color}`,borderRadius:'0 10px 10px 0',
                    transition:'all 0.15s'}}>
                <span style={{fontSize:20,color}}>{icon}</span>
                <div style={{flex:1}}>
                    <div style={{fontWeight:900,fontSize:15,color:'#1a1a2e'}}>{title}</div>
                    {subtitle && <div style={{fontSize:11,color:'#888',marginTop:1}}>{subtitle}</div>}
                </div>
                <span style={{
                    fontSize:18,color,fontWeight:700,
                    transform:collapsed?'rotate(-90deg)':'rotate(0deg)',
                    transition:'transform 0.2s',display:'inline-block',lineHeight:1
                }}>▾</span>
            </div>
            <div style={{
                overflow:'hidden',
                maxHeight:collapsed?0:'99999px',
                opacity:collapsed?0:1,
                transition:collapsed?'max-height 0.25s ease,opacity 0.15s ease':'max-height 0.4s ease,opacity 0.2s ease',
                marginBottom:collapsed?0:8,
            }}>
                <div style={{paddingTop:12}}>{children}</div>
            </div>
        </div>
    );
}

// ─── Per-card export hook ─────────────────────────────────────────────────────
function useNodeExport(nodeRef) {
    const [busy, setBusy] = useState(null);

    const capture = async (el, scale) => {
        const { default: h2c } = await import('html2canvas');
        const hidden = [];
        el.querySelectorAll('[data-xcard-btns]').forEach(n => {
            n._v = n.style.visibility; n.style.visibility = 'hidden'; hidden.push(n);
        });
        // Allow overflow so ECharts aren't clipped
        const origOverflow = el.style.overflow;
        el.style.overflow = 'visible';
        const rect   = el.getBoundingClientRect();
        const scrollW = Math.max(el.scrollWidth,  Math.ceil(rect.width));
        const scrollH = Math.max(el.scrollHeight, Math.ceil(rect.height));
        const canvas = await h2c(el, {
            scale, useCORS: true, allowTaint: true, backgroundColor: '#ffffff',
            logging: false, width: scrollW, height: scrollH,
            windowWidth: scrollW, windowHeight: scrollH, scrollX: 0, scrollY: 0,
            // Copy ECharts canvas pixels into cloned document
            onclone: (clonedDoc) => {
                const origs   = el.querySelectorAll('canvas');
                const cloneds = clonedDoc.querySelectorAll('canvas');
                origs.forEach((orig, i) => {
                    const cl = cloneds[i];
                    if (cl && orig.width > 0 && orig.height > 0) {
                        try { cl.width=orig.width; cl.height=orig.height; cl.getContext('2d').drawImage(orig,0,0); }
                        catch(e) { /* skip cross-origin */ }
                    }
                });
            },
        });
        el.style.overflow = origOverflow;
        hidden.forEach(n => { n.style.visibility = n._v || ''; delete n._v; });
        return canvas;
    };

    const exportPNG = async (filename) => {
        if (!nodeRef?.current || busy) return;
        setBusy('png');
        try {
            const canvas = await capture(nodeRef.current, 2.5);
            const a = document.createElement('a');
            a.download = filename + '.png';
            a.href = canvas.toDataURL('image/png');
            a.click();
        } catch(e) { console.error('PNG export failed', e); }
        finally { setBusy(null); }
    };

    const exportPDF = async (filename, title) => {
        if (!nodeRef?.current || busy) return;
        setBusy('pdf');
        try {
            const canvas  = await capture(nodeRef.current, 2.0);
            const mod     = await import('jspdf');
            const jsPDF   = mod.jsPDF || mod.default?.jsPDF || mod.default;
            const MG = 8, PAGE_W = 297, PAGE_H = 210, HDR = 12;
            const usableW = PAGE_W - MG * 2;
            const scale   = usableW / canvas.width;
            const imgH    = canvas.height * scale;
            const availH  = PAGE_H - HDR - 4;
            const pdf     = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const now     = new Date().toLocaleDateString('en-GB',{ day:'2-digit', month:'short', year:'numeric' });
            const drawHeader = (label) => {
                pdf.setFillColor(15,23,42); pdf.rect(0,0,PAGE_W,HDR,'F');
                pdf.setTextColor(255,255,255); pdf.setFont('helvetica','bold'); pdf.setFontSize(8);
                pdf.text(label, MG, 8.5);
                pdf.setFont('helvetica','normal'); pdf.setFontSize(7);
                pdf.text('Eswatini QoC Platform  |  ' + now, PAGE_W-MG, 8.5, { align:'right' });
            };
            // PNG preserves exact chart colors — JPEG loses them
            const toImg = (c) => c.toDataURL('image/png');
            drawHeader(title || filename);
            if (imgH <= availH) {
                pdf.addImage(toImg(canvas), 'PNG', MG, HDR+2, usableW, imgH);
            } else {
                const slicePx = Math.floor(availH / scale);
                let startPx=0, pageNum=1;
                while (startPx < canvas.height) {
                    if (pageNum > 1) { pdf.addPage('a4','landscape'); drawHeader(title||filename); }
                    const endPx = Math.min(startPx+slicePx, canvas.height);
                    const sc = document.createElement('canvas');
                    sc.width=canvas.width; sc.height=endPx-startPx;
                    sc.getContext('2d').drawImage(canvas,0,startPx,canvas.width,sc.height,0,0,canvas.width,sc.height);
                    pdf.addImage(toImg(sc), 'PNG', MG, HDR+2, usableW, sc.height*scale);
                    startPx=endPx; pageNum++;
                }
            }
            pdf.save(filename + '.pdf');
        } catch(e) { console.error('PDF export failed', e); }
        finally { setBusy(null); }
    };

    return { busy, exportPNG, exportPDF };
}

// ─── XCard — Card with built-in per-card PNG + PDF export ────────────────────
function XCard({ title, filename, children, extra, style, bodyStyle, size='small' }) {
    const ref  = useRef(null);
    const safe = (filename || (typeof title === 'string' ? title : 'chart')).replace(/[^a-zA-Z0-9_-]/g, '_');
    const label = typeof title === 'string' ? title : safe;
    const { busy, exportPNG, exportPDF } = useNodeExport(ref);

    const btns = (
        <span data-xcard-btns style={{ display:'inline-flex', gap:4, marginLeft:8 }}>
            <Tooltip title="Save as PNG">
                <span onClick={() => exportPNG(safe)}
                    style={{ cursor: busy==='png' ? 'wait' : 'pointer',
                        display:'inline-flex', alignItems:'center', gap:3,
                        padding:'2px 7px', borderRadius:4, fontSize:11,
                        border:'1px solid #d9d9d9', color:'#555', background:'#fafafa',
                        opacity: busy && busy!=='png' ? 0.4 : 1 }}>
                    {busy==='png' ? <LoadingOutlined style={{fontSize:10}}/> : <FileImageOutlined style={{fontSize:10}}/>}
                    PNG
                </span>
            </Tooltip>
            <Tooltip title="Save as PDF">
                <span onClick={() => exportPDF(safe, label)}
                    style={{ cursor: busy==='pdf' ? 'wait' : 'pointer',
                        display:'inline-flex', alignItems:'center', gap:3,
                        padding:'2px 7px', borderRadius:4, fontSize:11,
                        border:'1px solid #ffccc7', color:'#cf1322', background:'#fff2f0',
                        opacity: busy && busy!=='pdf' ? 0.4 : 1 }}>
                    {busy==='pdf' ? <LoadingOutlined style={{fontSize:10,color:'#cf1322'}}/> : <FilePdfOutlined style={{fontSize:10}}/>}
                    PDF
                </span>
            </Tooltip>
        </span>
    );

    return (
        <div ref={ref} data-pdf-block="true">
            <Card size={size} style={style} bodyStyle={bodyStyle}
                title={title}
                extra={<>{extra}{btns}</>}>
                {children}
            </Card>
        </div>
    );
}

// ─── Full-dashboard PDF export button ────────────────────────────────────────
function ExecDashPDF({ contentRef, filters = {} }) {
    const [exporting, setExporting] = useState(false);
    const [progress, setProgress]   = useState(0);

    const handleExport = async () => {
        if (exporting) return;
        setExporting(true); setProgress(0);
        let jsPDFLib, html2canvasLib;
        try {
            const [jspdfMod, h2cMod] = await Promise.all([ import('jspdf'), import('html2canvas') ]);
            jsPDFLib       = jspdfMod.jsPDF || jspdfMod.default?.jsPDF || jspdfMod.default;
            html2canvasLib = h2cMod.default || h2cMod;
        } catch(e) { import('antd').then(m=>m.message.error('PDF library failed: '+e.message)); setExporting(false); return; }

        setProgress(10);
        const el = contentRef?.current;
        if (!el) { setExporting(false); return; }

        try {
            const toHide = [];
            ['[data-pdf-hide]','.page-sidebar-wrapper','[data-xcard-btns]'].forEach(sel => {
                el.querySelectorAll(sel).forEach(n => {
                    if (n.style.visibility !== 'hidden') { n._v = n.style.visibility; n.style.visibility = 'hidden'; toHide.push(n); }
                });
            });
            setProgress(20);
            const canvas = await html2canvasLib(el, {
                scale:1.6, useCORS:true, allowTaint:true, backgroundColor:'#f4f6fb',
                scrollY:-window.scrollY, scrollX:0,
                windowWidth:el.scrollWidth, windowHeight:el.scrollHeight, logging:false,
            });
            toHide.forEach(n => { n.style.visibility = n._v || ''; delete n._v; });
            setProgress(75);

            const period = filters.period_label || 'All Periods';
            const generatedAt = new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'long',year:'numeric'});
            const PAGE_W=297, PAGE_H=210, MG=10, HEADER_H=13, FOOTER_H=9;
            const usableW = PAGE_W - MG*2;
            const usableH = PAGE_H - HEADER_H - FOOTER_H - 2;
            const scale   = usableW / canvas.width;
            const pageHeightPx = usableH / scale;

            const SECTION_IDS = ['sec-overview','sec-clauses','sec-mes','sec-qindex','sec-kpis','sec-surveys','sec-comparison'];
            const elRect = el.getBoundingClientRect();
            const sectionBreaks = SECTION_IDS.map(id => {
                const sec = el.querySelector(`#${id}`); if (!sec) return null;
                const r = sec.getBoundingClientRect();
                return (r.top - elRect.top + (el.scrollTop||0)) * 1.6;
            }).filter(Boolean).sort((a,b)=>a-b);

            const pages = []; let cursor = 0;
            while (cursor < canvas.height) {
                const idealEnd = cursor + pageHeightPx;
                if (idealEnd >= canvas.height) { pages.push({startPx:cursor,endPx:canvas.height}); break; }
                const leeway = pageHeightPx * 0.15;
                const best = sectionBreaks.filter(b=>b>cursor+pageHeightPx*0.5&&b<=idealEnd+leeway).sort((a,b)=>Math.abs(a-idealEnd)-Math.abs(b-idealEnd))[0];
                const endPx = best || idealEnd;
                pages.push({startPx:cursor,endPx}); cursor=endPx;
            }

            const totalPages = pages.length;
            const pdf = new jsPDFLib({orientation:'landscape',unit:'mm',format:'a4'});
            const drawChrome = (pn) => {
                pdf.setFillColor(15,23,42); pdf.rect(0,0,PAGE_W,HEADER_H,'F');
                pdf.setTextColor(255,255,255); pdf.setFontSize(8.5); pdf.setFont('helvetica','bold');
                pdf.text('ESWATINI MINISTRY OF HEALTH — National Quality Management Programme', MG, 8.5);
                pdf.setFont('helvetica','normal'); pdf.setFontSize(7.5);
                pdf.text(`Executive Dashboard  |  Period: ${period}  |  ${generatedAt}  |  Page ${pn} of ${totalPages}`, PAGE_W-MG, 8.5, {align:'right'});
                pdf.setFillColor(241,245,249); pdf.rect(0,PAGE_H-FOOTER_H,PAGE_W,FOOTER_H,'F');
                pdf.setTextColor(100,116,139); pdf.setFontSize(7);
                pdf.text('Confidential — Eswatini Ministry of Health QoC Platform', MG, PAGE_H-3);
                pdf.text(`${pn} / ${totalPages}`, PAGE_W-MG, PAGE_H-3, {align:'right'});
            };
            for (let i=0; i<pages.length; i++) {
                if (i>0) pdf.addPage('a4','landscape');
                drawChrome(i+1);
                const {startPx,endPx} = pages[i]; const sliceH=endPx-startPx;
                const sc=document.createElement('canvas'); sc.width=canvas.width; sc.height=sliceH;
                sc.getContext('2d').drawImage(canvas,0,startPx,canvas.width,sliceH,0,0,canvas.width,sliceH);
                pdf.addImage(sc.toDataURL('image/jpeg',0.88),'JPEG',MG,HEADER_H+1,usableW,sliceH*scale);
                setProgress(75+Math.round(((i+1)/pages.length)*22));
            }
            setProgress(97);
            pdf.save(`QoC_Executive_Dashboard_${period.replace(/[^A-Za-z0-9]/g,'_')}_${new Date().toISOString().slice(0,10)}.pdf`);
            setProgress(100);
            import('antd').then(m=>m.message.success(`PDF exported — ${totalPages} pages`));
            trailReportExport(TRAIL_MODULES.EXECUTIVE_DASH, 'Executive Dashboard', 'PDF', { period: filters.period_label || 'All', pages: totalPages });
        } catch(err) { import('antd').then(m=>m.message.error('Export failed: '+err.message)); console.error(err); }
        finally { setTimeout(()=>{setExporting(false);setProgress(0);},1500); }
    };

    return (
        <div style={{display:'inline-flex',alignItems:'center',gap:8}} data-pdf-hide="true">
            <Button type="primary" icon={exporting?<LoadingOutlined/>:<FilePdfOutlined/>}
                onClick={handleExport} disabled={exporting}
                style={{background:'#0f172a',borderColor:'#0f172a',fontWeight:600}}>
                {exporting ? `Exporting… ${progress}%` : 'Download PDF'}
            </Button>
            {exporting && <Progress percent={progress} size="small" style={{width:110,marginBottom:0}} strokeColor="#60a5fa" showInfo={false}/>}
        </div>
    );
}


// --- ClausesByStandard: one radar+bar+table per standard, scores never mixed ---
function ClausesByStandard({ clauseData, standards, allClauses, selectedStdId }) {
    const grouped = useMemo(() => {
        const stdsToShow = selectedStdId
            ? standards.filter(s => s.id === selectedStdId)
            : standards;
        return stdsToShow.map(std => {
            const stdClauses = allClauses.filter(c => c.standard_id === std.id);
            const cm = {};
            clauseData.forEach(r => {
                if (!r.clause_code) return;
                const cl = stdClauses.find(c => c.code === r.clause_code);
                if (!cl) return;
                if (!cm[r.clause_code]) cm[r.clause_code] = { scores: [], title: r.clause_title || cl.title };
                cm[r.clause_code].scores.push(parseFloat(r.avg_score || r.total_score || 0));
            });
            const clauses = stdClauses.map(cl => ({
                code: cl.code,
                title: cm[cl.code]?.title || cl.title,
                avg: cm[cl.code]
                    ? cm[cl.code].scores.reduce((a, b) => a + b, 0) / cm[cl.code].scores.length
                    : null,
            }));
            return { std, clauses };
        }).filter(g => g.clauses.some(c => c.avg !== null));
    }, [clauseData, standards, allClauses, selectedStdId]);

    if (!grouped.length) return null;

    return (
        <>
        {grouped.map(({ std, clauses }) => {
            const validClauses = clauses.filter(c => c.avg !== null);
            const accent = std.accent || '#1890ff';
            const barOpt = {
                tooltip: { trigger: 'axis' },
                grid: { top:10, right:80, bottom:30, left:55 },
                xAxis: { type: 'value', max: 100, axisLabel: { formatter: v => `${v}%`, fontSize: 9 } },
                yAxis: { type: 'category', data: validClauses.map(c => c.code), axisLabel: { fontSize: 11, fontWeight: 700 } },
                series: [{ type: 'bar', barMaxWidth: 18,
                    data: validClauses.map(c => {
                        const r = getRating(c.avg);
                        return { value: parseFloat(c.avg.toFixed(1)), itemStyle: { color: r.color, borderRadius: [0,5,5,0] } };
                    }),
                    label: { show: true, position: 'right', formatter: p => `${p.value}%`, fontSize: 10 },
                }],
            };
            const radarOpt = validClauses.length >= 3 ? {
                tooltip: { trigger: 'item' },
                radar: {
                    indicator: validClauses.map(c => ({ name: c.code, max: 100 })),
                    radius: '65%', center: ['50%', '52%'],
                    axisName: { fontSize: 11, fontWeight: 700 }, splitNumber: 5,
                    splitArea: { areaStyle: { color: ['#f8f9fa', '#fff'] } },
                },
                series: [{ type: 'radar', data: [{
                    value: validClauses.map(c => parseFloat(c.avg.toFixed(1))),
                    name: std.name,
                    areaStyle: { color: `${accent}30` },
                    lineStyle: { color: accent, width: 2 },
                    itemStyle: { color: accent },
                }] }],
            } : null;
            return (
                <div key={std.id} style={{ marginBottom: 28 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                        background: `${accent}12`, borderLeft: `5px solid ${accent}`,
                        borderRadius: '0 8px 8px 0', marginBottom: 12 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: accent,
                            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 900, fontSize: 14, flexShrink: 0 }}>
                            {std.name?.[0]?.toUpperCase()}
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 800, fontSize: 14, color: accent }}>{std.name}</div>
                            <div style={{ fontSize: 11, color: '#888' }}>{std.description || std.category || ''}</div>
                        </div>
                        <Tag style={{ background: accent, color: '#fff', border: 'none' }}>
                            {validClauses.length} clauses assessed
                        </Tag>
                    </div>
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                        {radarOpt && (
                            <Col span={10}>
                                <XCard title={`${std.name} — Radar`}
                                       filename={`Radar_${(std.name||'').replace(/\s+/g,'_')}`}>
                                    <ReactECharts option={radarOpt} style={{ height: 260 }}/>
                                </XCard>
                            </Col>
                        )}
                        <Col span={radarOpt ? 14 : 24}>
                            <XCard title={`${std.name} — Clause Scores`}
                                   filename={`Clauses_${(std.name||'').replace(/\s+/g,'_')}`}>
                                <ReactECharts option={barOpt}
                                    style={{ height: Math.max(180, validClauses.length * 34 + 40) }}/>
                            </XCard>
                        </Col>
                    </Row>
                    <XCard title={`${std.name} — Clause Detail`}
                           filename={`Detail_${(std.name||'').replace(/\s+/g,'_')}`}
                           style={{ marginBottom: 8 }}>
                        <Table size="small" pagination={false} dataSource={validClauses} rowKey="code"
                            columns={[
                                { title: 'Code', dataIndex: 'code', width: 80,
                                  render: c => <Tag style={{ background: accent, color: '#fff', border: 'none', fontWeight: 700 }}>{c}</Tag> },
                                { title: 'Title', dataIndex: 'title', ellipsis: true },
                                { title: 'Avg Score', dataIndex: 'avg', width: 100, align: 'center',
                                  render: v => { if (v===null) return '—'; const r=getRating(v); return <span style={{ fontWeight:900, color:r.color }}>{v.toFixed(1)}%</span>; } },
                                { title: 'Stars', width: 90, align: 'center',
                                  render: (_,r) => { if (r.avg===null) return '—'; return <Stars count={getRating(r.avg).stars} size={11}/>; } },
                                { title: 'Rating', width: 150, align: 'center',
                                  render: (_,r) => { if (r.avg===null) return '—'; const rt=getRating(r.avg); return <Tag style={{ background:rt.color, color:'#fff', border:'none', fontSize:10 }}>{rt.label}</Tag>; } },
                                { title: 'Progress', width: 140,
                                  render: (_,r) => { if (r.avg===null) return '—'; const rt=getRating(r.avg); return <Progress percent={Math.min(100,r.avg)} strokeColor={rt.color} showInfo={false} size="small"/>; } },
                            ]}/>
                    </XCard>
                </div>
            );
        })}
        </>
    );
}

// ─── Period Comparison sub-component (up to 5 cycles) ───────────────────────
function DeltaBadge({ delta }) {
    if (delta === null || delta === undefined) return <span style={{color:'#ccc'}}>—</span>;
    const d = parseFloat(delta);
    if (Math.abs(d) < 0.1) return <Tag icon={<MinusOutlined/>} color="default" style={{fontSize:11}}>No change</Tag>;
    if (d > 0) return <Tag icon={<RiseOutlined/>} color="success" style={{fontSize:11,fontWeight:700}}>+{d.toFixed(1)}%</Tag>;
    return <Tag icon={<FallOutlined/>} color="error" style={{fontSize:11,fontWeight:700}}>{d.toFixed(1)}%</Tag>;
}

const CYCLE_COLORS = ['#1890ff','#52c41a','#fa8c16','#722ed1','#eb2f96'];

// Receives filters directly from the main dashboard, plus resolvedFacilityIds/
// resolvedClauseCodes (already computed by the parent from the multi-select
// region/level/facility/clause filters) — no own facility/level/clause filter
// UI here, and no separate resolution logic; it just uses what the parent
// already worked out, the same values every other section on the dashboard
// uses. That's what keeps this comparison in scope with everything else
// rather than comparing against a differently-filtered dataset.
function PeriodComparison({ periods = [], filters = {}, standards = [], allClauses = [], resolvedFacilityIds = [], resolvedClauseCodes = [] }) {
    // Up to 5 selected period labels
    const [selected, setSelected] = useState([]);
    const [loading,  setLoading]  = useState(false);
    // dataMap: { [periodLabel]: [...clauseScoreRows] }
    const [dataMap,  setDataMap]  = useState({});
    // overallMap: { [periodLabel]: nationalAverageOrNull } -- sourced from
    // getComparisonReport, the same endpoint the main dashboard trusts.
    const [overallMap, setOverallMap] = useState({});

    // Default: select up to 5 most recent periods on mount
    useEffect(() => {
        if (periods.length > 0 && selected.length === 0) {
            setSelected(periods.slice(0, Math.min(5, periods.length)).map(p => p.label));
        }
    }, [periods]); // eslint-disable-line

    // Dependency array uses .join(',') on the resolved arrays rather than the
    // raw arrays themselves — arrays are compared by reference, so relying on
    // the array itself here could either miss real changes or re-fire
    // needlessly depending on how the parent recomputes them each render.
    // Joining to a string gives a stable, genuinely-comparable value.
    useEffect(() => {
        if (selected.length > 0) load();
    }, [selected, resolvedFacilityIds.join(','), resolvedClauseCodes.join(','), filters.standard_id]); // eslint-disable-line

    const load = async () => {
        setLoading(true);
        try {
            const results = await Promise.all(
                selected.map(label => {
                    const pd = periods.find(p => p.label === label);
                    const f  = {};
                    if (pd?.period_start) f.from_date    = pd.period_start;
                    if (pd?.period_end)   f.to_date      = pd.period_end;
                    f.period_label = label;
                    // facility_id and clause_code both accept comma-lists server-side
                    // (see Qoc_model's multi-select fixes) — sending the resolved
                    // list here is what makes this comparison respect the SAME
                    // region/level/facility/clause selection as the rest of the
                    // dashboard, not just a facility_id-only subset of it.
                    if (resolvedFacilityIds.length)  f.facility_id  = resolvedFacilityIds.join(',');
                    if (resolvedClauseCodes.length)   f.clause_code  = resolvedClauseCodes.join(',');
                    return QoCService.getClauseScoresAcrossFacilities(f)
                        .then(r => ({ label, data: r.data?.data || [] }))
                        .catch(() => ({ label, data: [] }));
                })
            );
            const map = {};
            results.forEach(({ label, data }) => {
                // Segregate by standard — never mix scores across standards
                map[label] = filters.standard_id
                    ? data.filter(r => {
                        const cl = allClauses.find(c => c.code === r.clause_code);
                        return cl?.standard_id === filters.standard_id;
                    })
                    : data;
            });
            setDataMap(map);

            // Fetch the "overall" national-average score from getComparisonReport --
            // the SAME endpoint and SAME field (AVG of qoc_assessments.overall_percentage)
            // that the main Executive Dashboard uses for its "National Average Rating".
            // This guarantees the Period Comparison banner always matches the main
            // dashboard for the same period, rather than re-deriving a different number
            // from clause-score rows (which use a different source and can diverge).
            const overallResults = await Promise.all(
                selected.map(label => {
                    const cf = { period_label: label };
                    if (resolvedFacilityIds.length) cf.facility_id = resolvedFacilityIds.join(',');
                    return QoCService.getComparisonReport(cf)
                        .then(r => ({ label, comp: r.data?.data || [] }))
                        .catch(() => ({ label, comp: [] }));
                })
            );
            const oMap = {};
            overallResults.forEach(({ label, comp }) => {
                const scores = comp.map(f => getFacilityScore(f, getScoringMode())).filter(v => v > 0);
                oMap[label] = scores.length ? scores.reduce((a,b)=>a+b,0)/scores.length : null;
            });
            setOverallMap(oMap);
        } catch(e) { console.error(e); }
        finally   { setLoading(false); }
    };

    // Aggregate clause avg per period
    const clauseAvg = (data) => {
        const cm = {};
        data.forEach(r => {
            if (!r.clause_code) return;
            if (!cm[r.clause_code]) cm[r.clause_code] = [];
            cm[r.clause_code].push(parseFloat(r.avg_score || r.total_score || 0));
        });
        const out = {};
        Object.entries(cm).forEach(([code, scores]) => {
            out[code] = scores.reduce((a,b)=>a+b,0)/scores.length;
        });
        return out;
    };

    // Aggregate facility avg per period
    const facAvg = (data) => {
        const fm = {};
        data.forEach(r => {
            if (!r.facility_id) return;
            if (!fm[r.facility_id]) fm[r.facility_id] = { name: r.facility_name || r.facility_id, scores:[] };
            const s = parseFloat(r.avg_score || r.total_score || 0);
            if (s > 0) fm[r.facility_id].scores.push(s);
        });
        const out = {};
        Object.entries(fm).forEach(([fid, d]) => {
            out[fid] = { name: d.name, avg: d.scores.reduce((a,b)=>a+b,0)/Math.max(d.scores.length,1) };
        });
        return out;
    };

    // Clauses to display in charts — restricted to selected standard if active,
    // and to the resolved clause selection if any clauses are checked.
    const displayClauses = useMemo(() => {
        let cls = filters.standard_id
            ? allClauses.filter(c => c.standard_id === filters.standard_id)
            : allClauses;
        if (resolvedClauseCodes.length) {
            cls = cls.filter(c => resolvedClauseCodes.includes(c.code));
        }
        return cls;
    }, [allClauses, filters.standard_id, resolvedClauseCodes]);

    // Pre-compute per-period summaries
    const periodSummaries = selected.map((label, i) => {
        const data  = dataMap[label] || [];
        const cl    = clauseAvg(data);
        const fac   = facAvg(data);
        // Overall score is fetched from getComparisonReport via overallMap (see load()),
        // NOT re-derived from clause-score rows. This is the same source and same field
        // (AVG of qoc_assessments.overall_percentage) the main Executive Dashboard uses,
        // so the banner here always matches the main dashboard's number for the same
        // period instead of silently diverging due to different underlying queries.
        const overall = overallMap[label] != null ? overallMap[label] : null;
        return { label, color: CYCLE_COLORS[i], cl, fac, overall, hasData: data.length > 0 };
    });

    // ── Clause bar chart: grouped bars, one group per clause ──────────────────
    const clauseBarOpt = () => ({
        tooltip: { trigger:'axis' },
        legend: {
            data: selected,
            bottom: 0,
            textStyle: { fontSize:11 },
        },
        grid: { top:20, right:20, bottom:55, left:55 },
        xAxis: { type:'category', data: displayClauses.map(c=>c.code), axisLabel:{fontSize:10} },
        yAxis: { type:'value', min:0, max:100, axisLabel:{formatter:v=>`${v}%`,fontSize:10} },
        series: periodSummaries.map(ps => ({
            name:       ps.label,
            type:       'bar',
            barMaxWidth: Math.max(8, Math.floor(40 / Math.max(selected.length,1))),
            data:       displayClauses.map(c => ({
                value:     parseFloat((ps.cl[c.code]||0).toFixed(1)),
                itemStyle: { color: ps.color, borderRadius:[3,3,0,0] },
            })),
            label: { show: selected.length <= 3, position:'top', fontSize:8,
                formatter: p => p.value > 0 ? `${p.value}%` : '' },
        })),
    });

    // ── Overall trend line ─────────────────────────────────────────────────────
    const trendLineOpt = () => ({
        tooltip: { trigger:'axis' },
        legend: { data: selected, bottom:0, textStyle:{fontSize:11} },
        grid:   { top:20, right:20, bottom:55, left:55 },
        xAxis:  { type:'category', data: displayClauses.map(c=>c.code), axisLabel:{fontSize:10} },
        yAxis:  { type:'value', min:0, max:100, axisLabel:{formatter:v=>`${v}%`,fontSize:10} },
        series: periodSummaries.map(ps => ({
            name:      ps.label,
            type:      'line',
            smooth:    true,
            symbolSize: 6,
            data:      displayClauses.map(c => parseFloat((ps.cl[c.code]||0).toFixed(1))),
            lineStyle: { color: ps.color, width:2 },
            itemStyle: { color: ps.color },
            areaStyle: { color: ps.color, opacity: 0.06 },
        })),
    });

    // ── Facility delta vs first selected period ────────────────────────────────
    const facilityDeltaOpt = () => {
        if (periodSummaries.length < 2) return {};
        const base = periodSummaries[0];
        // Collect all fids that appear in at least base + one other
        const allFids = new Set(Object.keys(base.fac));
        periodSummaries.slice(1).forEach(ps => Object.keys(ps.fac).forEach(f => allFids.add(f)));

        const rows = [...allFids].map(fid => {
            const baseScore = base.fac[fid]?.avg;
            const name = (base.fac[fid] || periodSummaries.find(ps=>ps.fac[fid])?.fac[fid])?.name || fid;
            return { fid, name, baseScore };
        }).filter(r => r.baseScore > 0);

        return {
            tooltip: { trigger:'axis' },
            legend: { data: selected.slice(1), bottom:0, textStyle:{fontSize:11} },
            grid:   { top:10, right:80, bottom:45, left:150 },
            xAxis:  { type:'value', axisLabel:{formatter:v=>`${v>0?'+':''}${v.toFixed(1)}%`,fontSize:9} },
            yAxis:  { type:'category', data:rows.map(r=>r.name.slice(0,22)).reverse(), axisLabel:{fontSize:9} },
            series: periodSummaries.slice(1).map((ps, i) => ({
                name:       ps.label,
                type:       'bar',
                barMaxWidth: 10,
                data: rows.map(r => {
                    const delta = ps.fac[r.fid]?.avg != null ? ps.fac[r.fid].avg - r.baseScore : null;
                    return delta !== null
                        ? { value: parseFloat(delta.toFixed(1)), itemStyle:{ color: ps.color, borderRadius: delta>=0?[0,4,4,0]:[4,0,0,4] } }
                        : { value: 0, itemStyle:{ opacity:0 } };
                }).reverse(),
                label: { show:true, position:'right', fontSize:8,
                    formatter: p => p.value !== 0 ? `${p.value>0?'+':''}${p.value}%` : '' },
            })),
        };
    };

    // ── Clause table: one column per period ───────────────────────────────────
    const clauseTableCols = [
        { title:'Clause', dataIndex:'clause', width:70,
          render:(c,row)=><Tag style={{background:row.color,color:'#fff',border:'none',fontWeight:700}}>{c}</Tag> },
        { title:'Standard', dataIndex:'stdName', width:110, ellipsis:true,
          render:(t,row)=><span style={{color:row.color,fontWeight:600,fontSize:11}}>{t}</span> },
        { title:'Title', dataIndex:'title', ellipsis:true },
        ...periodSummaries.map((ps, i) => ({
            title: <span style={{color:ps.color,fontWeight:700}}>{ps.label}</span>,
            dataIndex: `score_${i}`,
            width: 100,
            align: 'center',
            sorter: (a,b) => (a[`score_${i}`]||0)-(b[`score_${i}`]||0),
            render: v => v != null
                ? <div>
                    <span style={{fontWeight:900,color:getRating(v).color,fontSize:13}}>{v.toFixed(1)}%</span>
                    <div><Stars count={getRating(v).stars} size={9}/></div>
                  </div>
                : <span style={{color:'#ccc'}}>—</span>,
        })),
        // Change col: last period vs first period
        ...(periodSummaries.length >= 2 ? [{
            title: <span style={{color:'#888'}}>
                Δ {periodSummaries[periodSummaries.length-1].label.slice(-7)} vs {periodSummaries[0].label.slice(-7)}
            </span>,
            dataIndex: 'delta_last_first',
            width: 120,
            align: 'center',
            sorter: (a,b) => (a.delta_last_first||0)-(b.delta_last_first||0),
            render: v => <DeltaBadge delta={v}/>,
        }] : []),
    ];

    const clauseTableData = displayClauses.map(cl => {
        const std = standards.find(s => s.id === cl.standard_id);
        const row = { key:cl.id||cl.code, clause:cl.code, title:cl.title, color:std?.accent||'#1890ff', stdName: std?.name||'—' };
        periodSummaries.forEach((ps, i) => {
            row[`score_${i}`] = ps.cl[cl.code] ?? null;
        });
        const first = periodSummaries[0]?.cl[cl.code];
        const last  = periodSummaries[periodSummaries.length-1]?.cl[cl.code];
        row.delta_last_first = (first!=null && last!=null) ? last - first : null;
        return row;
    }).filter(row => periodSummaries.some((ps,i) => row[`score_${i}`] !== null));

    // ── Facility table: one column per period ─────────────────────────────────
    const allFacIds = new Set(periodSummaries.flatMap(ps => Object.keys(ps.fac)));
    const facTableData = [...allFacIds].map(fid => {
        const row = { key:fid };
        const realName = periodSummaries.map(ps => ps.fac[fid]?.name).find(n => n) || fid;
        row.name = realName;
        periodSummaries.forEach((ps, i) => {
            row[`score_${i}`] = ps.fac[fid]?.avg ?? null;
        });
        const scoredPeriods = periodSummaries
            .map((ps, i) => ({ i, avg: ps.fac[fid]?.avg }))
            .filter(p => p.avg != null);
        if (scoredPeriods.length >= 2) {
            const first = scoredPeriods[0].avg;
            const last  = scoredPeriods[scoredPeriods.length-1].avg;
            row.delta = last - first;
        } else {
            row.delta = null;
        }
        return row;
    }).sort((a,b)=>(b[`score_0`]||0)-(a[`score_0`]||0));

    const facTableCols = [
        { title:'Facility', dataIndex:'name', ellipsis:true, width:200 },
        ...periodSummaries.map((ps, i) => ({
            title: <span style={{color:ps.color,fontWeight:700}}>{ps.label}</span>,
            dataIndex: `score_${i}`,
            width: 100,
            align: 'center',
            render: v => v != null
                ? <span style={{fontWeight:700,color:getRating(v).color}}>{v.toFixed(1)}%</span>
                : <span style={{color:'#ccc'}}>—</span>,
        })),
        ...(periodSummaries.length >= 2 ? [{
            title: 'Trend',
            dataIndex: 'delta',
            width: 120,
            align: 'center',
            sorter: (a,b) => (a.delta||0)-(b.delta||0),
            defaultSortOrder: 'descend',
            render: v => <DeltaBadge delta={v}/>,
        }] : []),
    ];

    const hasAnyData = periodSummaries.some(ps => ps.hasData);
    const filterHints = [
        filters.standard_id && `standard: ${standards.find(s=>s.id===filters.standard_id)?.name||''}`,
        resolvedFacilityIds.length > 0 && `${resolvedFacilityIds.length} facilit${resolvedFacilityIds.length>1?'ies':'y'}`,
        resolvedClauseCodes.length > 0 && `${resolvedClauseCodes.length} claus${resolvedClauseCodes.length>1?'es':'e'}`,
    ].filter(Boolean);

    return (
        <Spin spinning={loading}>
            {/* Compact period selector row — up to 5 cycles */}
            <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',
                marginBottom:16,padding:'10px 16px',background:'#f8faff',
                borderRadius:8,border:'1px solid #e8f0fe'}}>
                <span style={{fontSize:12,color:'#888',fontWeight:600,whiteSpace:'nowrap'}}>
                    Cycles to compare:
                </span>
                {[0,1,2,3,4].map(i => (
                    <div key={i} style={{display:'flex',alignItems:'center',gap:4}}>
                        <span style={{
                            width:16,height:16,borderRadius:'50%',flexShrink:0,
                            background: selected[i] ? CYCLE_COLORS[i] : '#e0e0e0',
                            display:'inline-block'
                        }}/>
                        <Select
                            size="small"
                            style={{width:150}}
                            value={selected[i] || null}
                            placeholder={`Cycle ${i+1}`}
                            allowClear={i > 0}
                            onChange={val => {
                                const next = [...selected];
                                if (val) {
                                    next[i] = val;
                                } else {
                                    next.splice(i, 1);
                                }
                                setSelected(next.filter(Boolean));
                            }}>
                            {periods.filter(p => !p.label.startsWith('ENHEA')).map(p => (
                                <Option key={p.label} value={p.label}
                                    disabled={selected.includes(p.label) && selected[i] !== p.label}>
                                    {p.is_current==1?'★ ':''}{p.label}
                                </Option>
                            ))}
                        </Select>
                    </div>
                ))}
                {filterHints.length > 0 && (
                    <Tag color="geekblue" style={{marginLeft:4}}>🔗 {filterHints.join(' · ')}</Tag>
                )}
            </div>

            {!hasAnyData && !loading && (
                <Alert message="Select periods above to compare. Region, level, facility, and clause filters from the main bar apply automatically." type="info" showIcon/>
            )}

            {hasAnyData && (
                <>
                {/* Clause grouped bar chart */}
                <XCard title="Clause Scores — All Cycles" filename="Cycles_Clause_Bar" style={{marginBottom:16}}>
                    <ReactECharts option={clauseBarOpt()} style={{height:320}}/>
                </XCard>

                {/* Trend lines per clause */}
                <XCard title="Clause Trend Lines — Across Cycles" filename="Cycles_Clause_Trend" style={{marginBottom:16}}>
                    <ReactECharts option={trendLineOpt()} style={{height:280}}/>
                </XCard>

                {/* Clause table */}
                <XCard title="Clause-by-Clause — All Cycles" filename="Cycles_Clause_Table" style={{marginBottom:16}}>
                    <Table size="small" dataSource={clauseTableData} rowKey="key"
                        pagination={false} scroll={{x:'max-content'}}
                        columns={clauseTableCols}/>
                </XCard>

                {/* Facility delta vs first period */}
                {periodSummaries.length >= 2 && Object.keys(periodSummaries[0].fac).length > 0 && (
                    <XCard title={`Facility Change vs ${periodSummaries[0].label}`} filename="Cycles_Facility_Delta" style={{marginBottom:16}}>
                        <div style={{fontSize:11,color:'#888',marginBottom:8}}>
                            Delta of each subsequent cycle compared to <strong>{periodSummaries[0].label}</strong> (baseline)
                        </div>
                        <ReactECharts option={facilityDeltaOpt()} style={{height:Math.max(250, Math.min(facTableData.length,20)*20+80)}}/>
                    </XCard>
                )}

                {/* Facility table */}
                <XCard title="Facility Scores — All Cycles" filename="Cycles_Facility_Table">
                    <Table size="small" dataSource={facTableData} rowKey="key"
                        pagination={{pageSize:20,size:'small'}}
                        scroll={{x:'max-content'}}
                        columns={facTableCols}/>
                </XCard>
                </>
            )}
        </Spin>
    );
}

function OnlineUsersPanel() {
    const [users, setUsers]     = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen]       = useState(false);
    const timerRef              = useRef();

    const load = async () => {
        setLoading(true);
        try {
            const axiosInst = (await import('../../utils/axios')).default;
            const res = await axiosInst.get('online-users');
            setUsers(res.data?.data || []);
        } catch(e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        if (open) { load(); timerRef.current = setInterval(load, 30000); }
        else { clearInterval(timerRef.current); }
        return () => clearInterval(timerRef.current);
    }, [open]);

    const onlineUsers  = users.filter(u => parseInt(u.is_online) === 1);
    const offlineUsers = users.filter(u => parseInt(u.is_online) !== 1);

    const UserRow = ({ u, online }) => (
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 0', borderBottom:'1px solid #f0f0f0' }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:online?'#52c41a':'#d9d9d9', flexShrink:0 }}/>
            <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.name || u.username}</div>
                <div style={{ fontSize:10, color:'#888' }}>{u.role}{u.facility?' · '+u.facility.slice(0,22):''}</div>
            </div>
            <div style={{ fontSize:10, color:online?'#52c41a':'#aaa', flexShrink:0, whiteSpace:'nowrap' }}>
                {online ? 'Now' : u.last_login ? moment(u.last_login).fromNow() : 'Never'}
            </div>
        </div>
    );

    const content = (
        <div style={{ width:340, maxHeight:500, overflowY:'auto' }}>
            <Spin spinning={loading}>
                {onlineUsers.length > 0 && (
                    <div style={{ marginBottom:12 }}>
                        <div style={{ fontSize:11, fontWeight:800, color:'#52c41a', marginBottom:6, textTransform:'uppercase', letterSpacing:0.5 }}>🟢 Online ({onlineUsers.length})</div>
                        {onlineUsers.map(u => <UserRow key={u.id} u={u} online/>)}
                    </div>
                )}
                {offlineUsers.length > 0 && (
                    <div>
                        <div style={{ fontSize:11, fontWeight:800, color:'#aaa', marginBottom:6, textTransform:'uppercase', letterSpacing:0.5 }}>⚫ Recently Active ({offlineUsers.length})</div>
                        {offlineUsers.slice(0,15).map(u => <UserRow key={u.id} u={u} online={false}/>)}
                        {offlineUsers.length > 15 && <div style={{ fontSize:11, color:'#aaa', textAlign:'center', padding:'6px 0' }}>+{offlineUsers.length-15} more</div>}
                    </div>
                )}
                {!loading && users.length === 0 && <Empty description="No users found" image={Empty.PRESENTED_IMAGE_SIMPLE}/>}
            </Spin>
        </div>
    );

    return (
        <Popover
            title={<div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <span style={{ fontWeight:800 }}>👥 Platform Users</span>
                <Button size="small" icon={<ReloadOutlined/>} onClick={load} loading={loading} type="text"/>
            </div>}
            content={content}
            trigger="click"
            open={open}
            onOpenChange={setOpen}
            placement="bottomRight"
        >
            <div style={{ cursor:'pointer', display:'flex', alignItems:'center', gap:6, padding:'4px 10px', borderRadius:6, background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)' }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:'#52c41a', boxShadow:'0 0 6px #52c41a', animation:'pulse 2s infinite' }}/>
                <span style={{ fontSize:12, color:'#fff', fontWeight:600 }}>
                    {users.filter(u=>parseInt(u.is_online)===1).length > 0 ? `${users.filter(u=>parseInt(u.is_online)===1).length} online` : 'Users'}
                </span>
            </div>
        </Popover>
    );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
// EswatiniMap now supports MULTI-region selection: filters.region is an array,
// a region is "dimmed" only when the array is non-empty AND doesn't include
// it, and clicking a region toggles its membership in the array (via the
// toggleRegion callback) rather than replacing the whole selection.
const EswatiniMap = ({ regionalData, filters, toggleRegion }) => {
    const getScore = (r) => parseFloat(((regionalData||[]).find(x=>x.region===r)||{}).avg_score||0).toFixed(1);
    const getCount = (r) => ((regionalData||[]).find(x=>x.region===r)||{}).facility_count||0;
    const COLORS = {Hhohho:'#1565C0',Manzini:'#B71C1C',Lubombo:'#4A148C',Shiselweni:'#4E342E'};
    const getColor = (r) => parseFloat(getScore(r))===0 ? '#cbd5e1' : (COLORS[r]||'#94a3b8');
    const selectedRegions = filters.region || [];
    const getOpacity = (r) => (selectedRegions.length===0 || selectedRegions.includes(r)) ? 1 : 0.3;
    return (
        <div style={{display:'flex',gap:16,alignItems:'flex-start',flexWrap:'wrap',justifyContent:'center',width:'100%'}}>
            <svg viewBox="15 15 570 660" xmlns="http://www.w3.org/2000/svg" style={{flex:'1 1 280px',minWidth:240,maxWidth:500}}>
                <path d="M 228.7,267.7 L 227.9,271.1 L 225.7,272.6 L 225.3,273.9 L 224.7,280.4 L 225.3,282.5 L 222.1,284.6 L 227.1,288.3 L 218.9,298.3 L 220.6,305.0 L 218.8,307.8 L 219.0,310.0 L 216.7,313.1 L 217.1,320.9 L 206.2,322.5 L 206.2,323.8 L 209.7,326.1 L 209.1,327.2 L 206.9,326.9 L 208.6,328.8 L 206.9,329.0 L 206.7,330.5 L 204.8,327.5 L 201.9,327.3 L 200.4,331.1 L 198.4,331.9 L 199.3,333.3 L 198.8,334.5 L 197.3,334.1 L 196.6,335.7 L 194.6,335.5 L 194.3,337.8 L 193.6,336.9 L 192.4,337.1 L 193.0,339.2 L 191.5,338.5 L 189.4,339.3 L 190.0,340.9 L 188.9,347.8 L 182.5,336.8 L 183.5,335.8 L 177.4,333.4 L 176.7,319.8 L 175.5,322.4 L 173.2,324.0 L 171.1,323.7 L 169.1,324.5 L 167.9,321.9 L 169.0,321.1 L 168.8,320.3 L 162.9,321.2 L 162.6,317.9 L 153.9,321.3 L 150.2,318.8 L 142.8,318.9 L 131.7,300.5 L 106.3,288.6 L 93.5,280.6 L 83.4,277.1 L 79.9,274.4 L 77.2,273.8 L 73.8,268.4 L 98.3,247.6 L 139.7,174.1 L 148.0,156.6 L 149.2,156.7 L 148.1,156.5 L 158.7,135.8 L 164.3,111.3 L 219.5,66.3 L 239.9,40.0 L 279.8,30.0 L 417.7,116.1 L 417.5,117.0 L 413.0,120.4 L 413.7,122.7 L 411.3,124.0 L 409.5,123.8 L 408.4,122.1 L 405.1,123.2 L 402.5,122.8 L 401.7,121.9 L 403.3,120.3 L 402.3,119.4 L 393.3,121.7 L 390.8,123.4 L 386.8,122.6 L 382.7,119.7 L 381.3,119.5 L 379.5,121.2 L 379.0,150.9 L 384.2,192.7 L 384.7,193.8 L 386.5,194.1 L 388.1,197.4 L 386.7,204.4 L 383.6,203.1 L 384.1,205.6 L 383.4,206.5 L 378.6,208.4 L 370.8,208.7 L 369.2,211.2 L 367.7,210.3 L 364.9,211.5 L 363.7,209.0 L 363.6,209.8 L 361.2,209.3 L 358.5,211.7 L 353.8,209.6 L 351.2,210.2 L 350.2,209.0 L 349.0,209.4 L 348.4,211.6 L 347.1,210.4 L 344.9,210.3 L 343.7,208.4 L 341.0,208.1 L 340.6,208.6 L 342.2,210.9 L 339.9,211.0 L 335.2,214.0 L 330.8,214.9 L 332.6,218.5 L 331.9,221.8 L 333.3,224.4 L 332.8,225.1 L 329.6,225.3 L 328.9,230.2 L 323.5,230.0 L 322.0,228.1 L 320.2,229.4 L 317.0,229.3 L 319.1,231.4 L 318.4,232.2 L 323.8,234.4 L 324.7,236.1 L 319.4,236.0 L 317.1,239.3 L 319.8,241.7 L 314.8,245.7 L 311.4,244.5 L 307.0,245.0 L 306.5,244.3 L 308.4,242.5 L 307.9,242.0 L 301.5,242.6 L 297.6,245.8 L 298.1,247.8 L 296.0,249.9 L 291.3,249.7 L 289.9,251.1 L 288.6,250.4 L 287.0,255.2 L 283.1,256.7 L 279.9,259.8 L 277.8,260.1 L 276.6,261.5 L 274.3,259.2 L 275.4,256.9 L 273.9,255.4 L 271.9,256.5 L 269.5,256.1 L 269.1,257.8 L 266.6,259.3 L 262.7,255.6 L 263.1,253.4 L 259.9,253.3 L 259.3,252.4 L 255.8,255.3 L 252.3,254.3 L 250.9,254.8 L 248.8,253.7 L 248.2,255.6 L 248.8,256.7 L 248.1,257.0 L 241.9,253.7 L 239.6,254.0 L 235.8,252.6 L 235.7,255.6 L 237.2,255.7 L 237.5,256.7 L 236.8,259.1 L 234.2,259.0 L 233.2,258.5 L 233.5,257.5 L 230.2,257.4 L 227.8,260.0 L 229.1,263.8 L 228.7,267.7 Z" onClick={()=>toggleRegion('Hhohho')} style={{fill:getColor('Hhohho'),stroke:'#fff',strokeWidth:2.5,cursor:'pointer',opacity:getOpacity('Hhohho'),transition:'all 0.25s'}}><title>Hhohho</title></path>
                <path d="M 371.0,320.0 L 371.0,323.7 L 369.6,325.1 L 364.0,327.2 L 360.2,326.6 L 356.8,328.5 L 357.3,334.3 L 354.9,336.0 L 356.9,337.9 L 355.3,342.0 L 357.9,347.7 L 357.5,348.9 L 360.9,354.0 L 363.7,354.5 L 362.4,357.1 L 366.0,357.7 L 361.7,361.6 L 358.7,367.5 L 359.8,374.2 L 358.2,377.5 L 360.2,381.5 L 357.4,383.1 L 353.7,383.2 L 351.4,381.5 L 350.5,375.1 L 346.7,375.2 L 338.4,377.6 L 327.2,384.6 L 324.9,386.8 L 326.4,390.6 L 328.7,392.5 L 330.4,399.4 L 328.4,403.6 L 328.8,407.3 L 324.4,412.3 L 318.9,410.5 L 310.8,414.2 L 304.1,410.6 L 300.5,407.5 L 297.1,407.7 L 292.3,403.7 L 290.0,404.6 L 289.6,405.5 L 292.0,411.9 L 289.2,414.9 L 288.8,421.9 L 278.0,426.5 L 276.8,428.5 L 277.3,431.7 L 270.6,434.5 L 270.4,436.1 L 272.1,438.0 L 269.6,438.6 L 266.8,432.3 L 263.2,433.8 L 252.6,441.5 L 246.9,442.8 L 243.6,446.7 L 236.0,441.4 L 233.6,447.4 L 228.3,449.3 L 229.5,452.4 L 228.9,453.1 L 230.5,454.0 L 232.3,458.3 L 224.8,459.8 L 224.1,463.9 L 218.5,464.3 L 216.6,462.3 L 213.5,462.4 L 213.0,460.9 L 211.8,461.7 L 211.2,460.8 L 209.8,462.8 L 208.2,461.3 L 206.4,462.1 L 206.8,463.1 L 205.3,464.7 L 205.8,466.9 L 205.1,467.9 L 200.7,465.7 L 197.9,466.7 L 195.8,467.8 L 196.3,470.0 L 194.3,470.8 L 191.3,475.4 L 187.5,472.3 L 183.6,472.1 L 161.4,484.5 L 161.2,485.6 L 160.3,485.0 L 151.8,488.3 L 144.1,494.1 L 144.4,496.6 L 143.3,497.8 L 143.4,499.5 L 142.2,499.8 L 142.1,499.1 L 140.5,501.5 L 138.1,499.6 L 141.9,486.7 L 133.4,479.9 L 131.0,474.7 L 134.5,473.7 L 134.3,470.7 L 136.0,467.6 L 133.8,465.5 L 134.8,458.2 L 133.9,453.9 L 133.1,453.4 L 92.5,460.4 L 89.4,456.3 L 90.3,452.9 L 87.4,451.6 L 84.5,453.1 L 85.6,454.4 L 85.2,456.5 L 79.6,455.2 L 75.8,457.3 L 72.4,455.6 L 70.3,456.0 L 68.6,462.6 L 69.3,464.3 L 62.2,467.0 L 58.5,470.7 L 54.9,470.2 L 52.6,470.9 L 51.6,472.8 L 48.0,473.8 L 46.1,476.8 L 42.7,479.6 L 39.0,480.3 L 34.7,472.9 L 30.0,429.0 L 30.3,370.6 L 35.7,329.7 L 46.1,307.3 L 56.7,296.4 L 70.4,271.3 L 73.8,268.4 L 77.2,273.8 L 79.9,274.4 L 83.4,277.1 L 93.5,280.6 L 106.3,288.6 L 131.7,300.5 L 142.8,318.9 L 150.2,318.8 L 153.9,321.3 L 162.6,317.9 L 162.9,321.2 L 168.8,320.3 L 169.0,321.1 L 167.9,321.9 L 169.1,324.5 L 171.1,323.7 L 173.2,324.0 L 175.5,322.4 L 176.7,319.8 L 177.4,333.4 L 183.0,335.5 L 188.9,347.8 L 190.0,340.8 L 189.4,339.3 L 191.6,338.5 L 193.0,339.2 L 192.3,337.1 L 193.6,336.9 L 194.3,337.8 L 194.6,335.5 L 196.6,335.7 L 197.3,334.1 L 198.8,334.5 L 199.3,333.3 L 198.4,331.9 L 200.4,331.1 L 201.9,327.3 L 204.8,327.5 L 206.7,330.5 L 206.9,329.0 L 208.6,328.8 L 206.9,326.9 L 209.1,327.2 L 209.7,326.1 L 206.2,323.8 L 206.2,322.5 L 217.1,320.9 L 216.7,313.1 L 219.0,310.0 L 218.8,307.8 L 220.6,305.0 L 218.9,298.3 L 227.1,288.3 L 222.1,284.6 L 225.4,282.5 L 224.7,280.4 L 225.6,272.6 L 227.7,271.4 L 228.6,269.4 L 229.1,263.8 L 227.8,260.0 L 230.2,257.4 L 233.5,257.5 L 233.2,258.5 L 234.2,259.0 L 236.8,259.1 L 237.5,256.7 L 237.1,255.7 L 235.8,255.6 L 235.4,252.8 L 239.6,254.1 L 241.9,253.7 L 248.1,257.0 L 248.8,256.7 L 248.2,255.6 L 248.8,253.7 L 250.9,254.8 L 252.3,254.3 L 255.8,255.3 L 259.3,252.4 L 259.9,253.3 L 263.1,253.4 L 262.7,255.6 L 266.6,259.3 L 269.1,257.8 L 269.5,256.1 L 271.9,256.5 L 273.9,255.4 L 275.4,256.9 L 274.3,259.2 L 276.6,261.5 L 276.9,260.5 L 281.8,258.5 L 283.1,256.7 L 287.0,255.2 L 288.6,250.4 L 290.1,251.1 L 291.3,249.7 L 293.7,250.2 L 297.5,248.9 L 298.2,247.6 L 297.4,245.9 L 301.5,242.7 L 307.9,242.0 L 308.4,242.7 L 306.5,244.4 L 307.0,245.0 L 311.4,244.5 L 313.9,245.8 L 318.9,242.8 L 319.9,241.5 L 317.2,239.3 L 317.9,237.8 L 319.1,237.5 L 319.6,236.0 L 324.8,236.0 L 323.9,234.3 L 318.6,232.3 L 319.1,231.4 L 317.0,229.3 L 320.2,229.4 L 322.0,228.1 L 323.5,230.0 L 328.9,230.2 L 329.6,225.3 L 332.8,225.1 L 333.3,224.4 L 331.9,221.8 L 332.6,218.5 L 330.8,214.9 L 335.2,214.0 L 339.9,211.0 L 342.2,210.9 L 341.0,208.1 L 343.7,208.4 L 344.9,210.3 L 347.1,210.4 L 348.4,211.6 L 349.9,209.0 L 351.2,210.2 L 353.8,209.6 L 358.5,211.7 L 360.8,209.9 L 359.8,212.4 L 361.4,209.3 L 363.9,209.8 L 364.9,211.5 L 367.7,210.3 L 369.2,211.2 L 370.8,208.7 L 378.6,208.4 L 383.6,206.3 L 391.8,237.6 L 388.9,237.3 L 388.1,240.5 L 391.6,239.4 L 392.4,242.0 L 395.4,244.2 L 387.1,249.2 L 388.2,251.7 L 386.9,252.6 L 387.0,271.6 L 379.7,284.0 L 363.8,295.0 L 362.0,299.3 L 366.6,305.7 L 363.5,306.4 L 361.4,309.7 L 361.1,315.0 L 362.2,320.6 L 367.5,320.3 L 369.2,318.0 L 371.0,320.0 Z" onClick={()=>toggleRegion('Manzini')} style={{fill:getColor('Manzini'),stroke:'#fff',strokeWidth:2.5,cursor:'pointer',opacity:getOpacity('Manzini'),transition:'all 0.25s'}}><title>Manzini</title></path>
                <path d="M 456.1,566.8 L 458.9,551.1 L 396.3,561.6 L 359.7,547.2 L 353.6,534.6 L 351.8,533.4 L 349.1,534.3 L 345.0,533.1 L 347.9,530.8 L 342.0,530.1 L 342.7,532.1 L 339.9,532.7 L 339.2,532.1 L 340.6,530.8 L 338.4,529.1 L 336.5,531.3 L 336.5,532.5 L 335.1,532.2 L 334.6,530.2 L 333.3,530.2 L 331.2,532.0 L 328.6,531.4 L 329.6,520.7 L 327.7,522.9 L 325.2,523.2 L 325.2,520.3 L 323.7,517.4 L 323.0,518.5 L 321.2,518.0 L 320.8,517.0 L 323.0,516.2 L 323.8,514.9 L 320.9,513.5 L 319.5,505.8 L 314.9,502.4 L 314.7,499.9 L 317.3,495.7 L 319.1,494.6 L 319.7,492.6 L 318.6,488.4 L 321.2,485.8 L 322.4,487.3 L 321.6,488.8 L 322.6,489.1 L 322.5,491.0 L 325.9,489.0 L 327.2,490.3 L 330.1,490.5 L 328.9,493.5 L 331.6,493.7 L 332.6,492.0 L 329.4,486.7 L 330.9,483.3 L 334.0,482.6 L 335.7,480.6 L 341.5,482.6 L 344.5,481.6 L 346.5,482.4 L 347.8,477.6 L 346.7,473.1 L 350.3,473.4 L 353.8,469.1 L 352.1,466.5 L 352.2,462.7 L 348.3,460.1 L 344.6,460.8 L 343.3,462.4 L 342.5,460.8 L 343.6,458.5 L 342.9,457.7 L 344.2,456.8 L 340.5,450.7 L 337.4,449.5 L 336.3,443.4 L 332.0,444.6 L 327.8,442.8 L 325.0,438.9 L 325.2,437.8 L 322.1,434.4 L 321.3,430.2 L 323.8,424.0 L 322.8,423.1 L 321.0,424.6 L 318.5,424.5 L 316.3,423.2 L 318.2,416.8 L 318.5,410.6 L 324.4,412.3 L 329.1,407.1 L 328.2,406.2 L 328.4,403.7 L 330.4,399.4 L 328.8,392.6 L 326.4,390.6 L 325.1,386.7 L 327.3,384.6 L 332.5,382.0 L 338.5,377.6 L 346.5,375.2 L 350.5,375.2 L 351.4,381.6 L 353.9,383.1 L 357.4,383.1 L 360.2,381.5 L 358.2,377.5 L 359.8,374.2 L 358.7,367.5 L 362.5,360.4 L 366.1,357.5 L 366.4,358.1 L 365.6,357.3 L 362.6,357.4 L 363.7,354.5 L 360.9,354.0 L 357.5,348.9 L 357.9,347.7 L 355.3,342.0 L 356.9,337.9 L 355.0,336.0 L 357.3,334.3 L 356.5,329.2 L 357.8,327.7 L 360.2,326.6 L 364.6,327.1 L 368.4,324.9 L 369.6,325.1 L 371.0,323.7 L 371.0,320.0 L 369.4,318.0 L 361.6,320.7 L 362.3,318.6 L 361.1,315.0 L 361.4,309.8 L 363.5,306.4 L 366.6,305.7 L 362.0,299.3 L 364.0,295.0 L 379.7,284.0 L 387.0,271.6 L 386.9,252.6 L 388.2,251.7 L 387.1,249.2 L 395.4,244.2 L 392.4,242.0 L 391.6,239.4 L 388.1,240.5 L 388.9,237.3 L 391.8,237.6 L 383.6,206.3 L 383.6,203.1 L 386.8,204.2 L 388.2,197.6 L 386.5,194.2 L 384.7,193.8 L 384.2,192.7 L 379.0,150.9 L 379.5,121.2 L 381.4,119.5 L 386.8,122.7 L 390.8,123.4 L 393.3,121.7 L 402.3,119.4 L 403.3,120.3 L 401.7,121.9 L 402.5,122.8 L 405.1,123.2 L 408.4,122.1 L 409.5,123.8 L 411.3,124.0 L 413.5,123.0 L 412.9,120.5 L 416.3,118.4 L 417.7,116.1 L 461.4,142.9 L 503.8,124.2 L 514.7,141.8 L 517.9,140.5 L 525.6,143.2 L 548.3,146.5 L 548.1,158.1 L 555.5,207.1 L 543.1,265.2 L 544.3,305.1 L 567.4,353.9 L 567.3,479.0 L 561.6,478.3 L 559.4,474.0 L 557.0,471.9 L 556.0,466.8 L 553.1,464.8 L 542.3,466.9 L 537.9,469.8 L 535.3,470.3 L 531.9,469.8 L 528.1,466.1 L 525.7,465.3 L 518.1,467.1 L 519.8,471.9 L 512.9,511.3 L 513.4,514.8 L 511.1,532.4 L 508.0,545.8 L 504.3,608.5 L 475.6,608.7 L 478.6,600.5 L 458.0,600.6 L 459.9,584.9 L 451.6,584.9 L 454.3,574.1 L 456.5,572.9 L 456.1,566.8 Z" onClick={()=>toggleRegion('Lubombo')} style={{fill:getColor('Lubombo'),stroke:'#fff',strokeWidth:2.5,cursor:'pointer',opacity:getOpacity('Lubombo'),transition:'all 0.25s'}}><title>Lubombo</title></path>
                <path d="M 343.1,455.6 L 344.2,456.8 L 342.9,457.7 L 343.6,458.5 L 342.5,460.8 L 343.3,462.4 L 344.6,460.8 L 348.3,460.1 L 352.2,462.7 L 352.1,466.5 L 353.8,469.1 L 350.3,473.4 L 346.7,473.1 L 347.8,477.6 L 347.1,481.8 L 341.5,482.6 L 335.7,480.6 L 334.0,482.6 L 330.9,483.3 L 329.5,486.7 L 332.6,492.3 L 330.6,494.0 L 328.7,493.1 L 330.0,490.4 L 327.2,490.3 L 325.9,489.0 L 322.5,490.9 L 322.6,489.1 L 321.6,488.8 L 322.4,487.3 L 321.2,485.8 L 318.6,488.4 L 319.7,492.6 L 319.2,494.5 L 317.3,495.7 L 314.7,500.1 L 314.9,502.4 L 318.6,505.1 L 318.7,504.1 L 319.7,506.1 L 320.9,513.5 L 323.8,514.9 L 323.0,516.2 L 320.9,516.9 L 321.2,518.0 L 323.0,518.5 L 323.7,517.4 L 325.2,520.3 L 325.3,523.4 L 327.7,522.9 L 329.6,520.7 L 328.6,531.4 L 331.2,532.0 L 333.3,530.2 L 334.6,530.2 L 335.1,532.2 L 336.5,532.5 L 336.5,531.3 L 338.4,529.1 L 340.6,530.8 L 339.2,532.1 L 339.9,532.7 L 342.7,532.1 L 342.0,530.1 L 347.9,530.8 L 345.0,533.1 L 349.1,534.3 L 351.8,533.4 L 353.6,534.6 L 359.7,547.2 L 396.3,561.6 L 458.9,551.1 L 455.8,566.8 L 456.5,572.9 L 454.3,574.1 L 451.6,584.9 L 459.9,584.9 L 458.0,600.6 L 478.3,600.5 L 475.6,608.7 L 504.3,608.5 L 503.6,614.6 L 504.7,620.6 L 504.1,632.7 L 506.6,639.8 L 507.0,643.9 L 505.5,646.7 L 505.3,653.7 L 504.1,657.2 L 504.5,670.0 L 311.0,669.1 L 173.9,623.7 L 138.2,581.4 L 122.3,570.0 L 107.1,556.8 L 96.9,539.9 L 103.0,528.7 L 104.4,523.4 L 104.4,511.5 L 103.0,506.1 L 93.3,499.0 L 91.0,498.5 L 89.9,496.5 L 88.5,496.2 L 80.8,489.3 L 76.5,487.0 L 76.4,484.9 L 74.8,483.8 L 71.0,465.0 L 70.4,463.9 L 69.3,464.3 L 68.7,462.8 L 70.3,455.9 L 72.5,455.6 L 75.8,457.3 L 79.5,455.2 L 85.2,456.5 L 85.6,454.4 L 84.5,453.2 L 87.0,451.6 L 90.2,452.9 L 89.4,456.3 L 92.5,460.4 L 133.7,453.6 L 134.9,461.3 L 133.9,465.6 L 136.0,467.6 L 134.3,470.7 L 134.5,473.7 L 131.0,474.7 L 133.4,479.9 L 141.9,486.7 L 138.1,499.6 L 140.5,501.5 L 142.1,499.1 L 142.2,499.8 L 143.4,499.5 L 143.3,497.8 L 144.4,496.6 L 144.1,494.1 L 151.8,488.3 L 160.3,485.0 L 161.2,485.6 L 161.4,484.5 L 183.6,472.1 L 187.5,472.3 L 191.3,475.4 L 194.3,470.8 L 196.3,470.0 L 195.8,467.8 L 197.9,466.7 L 200.7,465.7 L 205.1,467.9 L 205.8,466.9 L 205.3,464.7 L 206.8,463.1 L 206.4,462.1 L 208.2,461.3 L 209.8,462.8 L 211.2,460.8 L 211.8,461.7 L 213.0,460.9 L 213.5,462.4 L 216.6,462.3 L 218.5,464.3 L 224.1,463.9 L 224.8,459.8 L 232.3,458.2 L 230.5,454.1 L 228.9,453.1 L 229.3,451.8 L 228.1,450.0 L 228.5,449.0 L 233.6,447.3 L 235.9,441.4 L 243.6,446.7 L 247.6,442.3 L 252.6,441.7 L 253.3,440.5 L 254.5,440.5 L 262.8,434.1 L 266.8,432.2 L 269.6,438.6 L 271.9,438.2 L 272.2,437.4 L 270.4,436.1 L 270.6,434.5 L 277.3,431.7 L 276.8,428.5 L 278.0,426.5 L 288.8,421.9 L 289.2,414.9 L 292.0,411.9 L 289.6,405.2 L 291.9,404.1 L 296.6,407.4 L 301.2,407.7 L 304.6,410.9 L 310.4,414.1 L 313.0,413.8 L 318.5,410.6 L 318.2,416.8 L 316.3,423.3 L 318.6,424.5 L 321.0,424.6 L 322.8,423.1 L 323.8,424.0 L 321.3,430.2 L 322.1,434.4 L 325.2,437.8 L 325.0,438.9 L 327.8,442.8 L 332.0,444.6 L 336.3,443.4 L 337.4,449.5 L 340.5,450.7 L 343.1,455.6 Z" onClick={()=>toggleRegion('Shiselweni')} style={{fill:getColor('Shiselweni'),stroke:'#fff',strokeWidth:2.5,cursor:'pointer',opacity:getOpacity('Shiselweni'),transition:'all 0.25s'}}><title>Shiselweni</title></path>
                <g style={{pointerEvents:'none'}}>
                    <text x="243" y="186" textAnchor="middle" style={{fontSize:24,fontWeight:800,fill:'#fff'}}>Hhohho</text>
                    <text x="243" y="214" textAnchor="middle" style={{fontSize:26,fontWeight:900,fill:'#fff'}}>{getScore('Hhohho')}%</text>
                    <text x="243" y="236" textAnchor="middle" style={{fontSize:20,fill:'rgba(255,255,255,0.85)'}}>{getCount('Hhohho')} fac.</text>
                </g>
                <g style={{pointerEvents:'none'}}>
                    <text x="200" y="366" textAnchor="middle" style={{fontSize:24,fontWeight:800,fill:'#fff'}}>Manzini</text>
                    <text x="200" y="394" textAnchor="middle" style={{fontSize:26,fontWeight:900,fill:'#fff'}}>{getScore('Manzini')}%</text>
                    <text x="200" y="416" textAnchor="middle" style={{fontSize:20,fill:'rgba(255,255,255,0.85)'}}>{getCount('Manzini')} fac.</text>
                </g>
                <g style={{pointerEvents:'none'}}>
                    <text x="450" y="351" textAnchor="middle" style={{fontSize:24,fontWeight:800,fill:'#fff'}}>Lubombo</text>
                    <text x="450" y="379" textAnchor="middle" style={{fontSize:26,fontWeight:900,fill:'#fff'}}>{getScore('Lubombo')}%</text>
                    <text x="450" y="401" textAnchor="middle" style={{fontSize:20,fill:'rgba(255,255,255,0.85)'}}>{getCount('Lubombo')} fac.</text>
                </g>
                <g style={{pointerEvents:'none'}}>
                    <text x="285" y="561" textAnchor="middle" style={{fontSize:24,fontWeight:800,fill:'#fff'}}>Shiselweni</text>
                    <text x="285" y="589" textAnchor="middle" style={{fontSize:26,fontWeight:900,fill:'#fff'}}>{getScore('Shiselweni')}%</text>
                    <text x="285" y="611" textAnchor="middle" style={{fontSize:20,fill:'rgba(255,255,255,0.85)'}}>{getCount('Shiselweni')} fac.</text>
                </g>
            </svg>
            <div style={{flex:'0 0 140px',display:'flex',flexDirection:'column',gap:8,paddingTop:16}}>
                <div style={{fontSize:11,fontWeight:700,color:'#64748b',textTransform:'uppercase',letterSpacing:1,marginBottom:4}}>Regions</div>
                {['Hhohho','Manzini','Lubombo','Shiselweni'].map(r=>(<div key={r} onClick={()=>toggleRegion(r)} style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',padding:'5px 8px',borderRadius:6,background:selectedRegions.includes(r)?'#f0f9ff':'transparent',border:selectedRegions.includes(r)?'1px solid #1890ff':'1px solid transparent'}}><div style={{width:14,height:14,borderRadius:3,flexShrink:0,background:{Hhohho:'#1565C0',Manzini:'#B71C1C',Lubombo:'#4A148C',Shiselweni:'#4E342E'}[r]}}/><div><div style={{fontSize:12,fontWeight:700,color:'#1e293b'}}>{r}</div><div style={{fontSize:11,color:'#64748b'}}>{getScore(r)}% {getCount(r)} fac.</div></div></div>))}
                {selectedRegions.length>0 && <button onClick={()=>toggleRegion(null)} style={{marginTop:4,padding:'5px 10px',borderRadius:6,border:'1px solid #e5e7eb',background:'#fff',cursor:'pointer',fontSize:11,color:'#64748b'}}>Clear region filter ({selectedRegions.length})</button>}
            </div>
        </div>
    );
}

export default function QoCExecutiveDashboard() {
    const lockedRegion = util.getLockedRegion ? util.getLockedRegion() : null;
    const dashboardRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [facilities, setFacilities] = useState([]);
    const [standards,  setStandards]  = useState([]);  // all standards
    const [allClauses, setAllClauses] = useState([]);  // all clauses across all standards
    const [clauses, setClauses]       = useState([]);
    const [dateRange, setDateRange]   = useState([moment().subtract(12,'months'), moment()]);
    // level, region, facility_id, and clause_id are all MULTI-SELECT arrays now
    // (previously single scalar values). standard_id and period_label stay
    // single-select — a report only makes sense for one period, and standards
    // are deliberately never mixed together (see the "scores never mixed"
    // comments throughout ClausesByStandard/PeriodComparison).
    const [scoringMode, setScoringMode] = useState(localStorage.getItem('qoc_scoring_mode') || 'benchmark');
    const [filters, setFilters] = useState({
        facility_id: [], level: [], clause_id: [], region: [], standard_id: null, period_label: null,
        from_date: moment().subtract(12,'months').format('YYYY-MM-DD'),
        to_date:   moment().format('YYYY-MM-DD'),
    });
    const [reportFacility, setReportFacility] = useState(null);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const [overview,   setOverview]   = useState({});
    const [trendData,  setTrend]      = useState([]);
    const [compData,   setComp]       = useState([]);
    const [distData,   setDist]       = useState([]);
    const [schedData,  setSched]      = useState({ data:[], summary:{} });
    const [clauseData, setClause]     = useState([]);
    const [subData,    setSub]        = useState([]);
    const [meData,     setME]         = useState([]);
    const [kpiData,    setKPI]        = useState({ kpis:[], actuals:[] });
    const [qiData,     setQI]         = useState([]);
    const [regionalData, setRegional]   = useState([]);
    const [heatmapPage, setHeatmapPage] = useState(0);
    const [qiPageSize, setQiPageSize]   = useState(20);
    const [qiSortAsc,  setQiSortAsc]    = useState(false);
    const [surveyData, setSurveyData] = useState({ surveys:[], stats:{}, by_facility:{} });
    const [periods,    setPeriods]    = useState([]);

    useEffect(() => {
        trailPageView(TRAIL_MODULES.EXECUTIVE_DASH, window.location.pathname);
        FacilityService.all({ status:1, ...(lockedRegion && { region: lockedRegion }) }).then(r => setFacilities(r.data?.result?.data||[]));
        QoCService.getStandards().then(r => setStandards(r.data?.data||[]));
        QoCService.getClauses().then(r => {
            const cls = r.data?.data || [];
            setAllClauses(cls);
            setClauses(cls);
        });
        QoCService.getPeriods().then(r => {
            const active = (r.data?.data||[]).filter(p => parseInt(p.is_active) === 1);
            setPeriods(active);
            const current = active.find(p => parseInt(p.is_current) === 1);
            if (current) {
                setFilters(f => ({ ...f, period_label: current.label, from_date: current.period_start, to_date: current.period_end }));
                setDateRange([moment(current.period_start), moment(current.period_end)]);
            }
        });
    }, []);

    useEffect(() => { load(); setHeatmapPage(0); }, [filters, scoringMode]); // eslint-disable-line
    useEffect(() => {
        const handler = (e) => { setScoringMode(e.detail.mode); };
        window.addEventListener('scoringModeChanged', handler);
        return () => window.removeEventListener('scoringModeChanged', handler);
    }, []);

    // ── Resolved filter scope ────────────────────────────────────────────────
    // Region, level, and explicit facility selection all narrow the SAME
    // underlying thing — which facilities are in scope — so rather than
    // teaching every backend endpoint to filter by region-array AND
    // level-array AND facility-array independently (several of which only
    // ever supported a single scalar value), we resolve all three down to one
    // concrete list of facility ids here, client-side, using the facility
    // list already loaded. That list is then sent as `facility_id` to every
    // backend call — which DOES already support a multi-value facility_id
    // (array or comma-list) across every endpoint this dashboard uses.
    // This is also exactly why the facility dropdown's own options are
    // filtered by region+level below: what's selectable there should always
    // match what's actually in scope.
    const resolvedFacilityIds = useMemo(() => {
        return facilities.filter(fac =>
            (!filters.region?.length || filters.region.includes(fac.located_region)) &&
            (!filters.level?.length  || filters.level.includes(fac.site_type)) &&
            (!filters.facility_id?.length || filters.facility_id.includes(fac.id))
        ).map(fac => fac.id);
    }, [facilities, filters.region, filters.level, filters.facility_id]);
    const isFacilityScoped = resolvedFacilityIds.length > 0 && resolvedFacilityIds.length < facilities.length;

    // Clause selection resolves to clause CODES (not ids) because that's what
    // the backend's multi-select-capable functions (getClauseScoresAcrossFacilities,
    // getQualityIndexData) accept — same reasoning as facility_id above, reusing
    // filter dimensions the backend already supports rather than adding new ones.
    const resolvedClauseCodes = useMemo(() => {
        return filters.clause_id?.length
            ? allClauses.filter(c => filters.clause_id.includes(c.id)).map(c => c.code)
            : [];
    }, [allClauses, filters.clause_id]);

    const load = async () => {
        setLoading(true);
        const f = {
            from_date:  filters.from_date,
            to_date:    filters.to_date,
            ...(isFacilityScoped && { facility_id: resolvedFacilityIds.join(',') }),
            ...(filters.standard_id  && { standard_id:  filters.standard_id }),
            ...(filters.period_label && { period_label: filters.period_label }),
        };
        // kf now ALSO carries clause_code — this is the actual "same
        // recalculations as the QoC report" piece: getQualityIndex previously
        // never received any clause filter at all, so Quality Index numbers on
        // this dashboard never reflected clause selection. With clause_code
        // wired through, a clause-scoped Quality Index genuinely recalculates
        // from qoc_clause_scores restricted to the selected clauses, the same
        // backend path the report generator uses — not a client-side
        // approximation.
        const kf = {
            ...(filters.period_label && { period_label: filters.period_label }),
            ...(filters.from_date    && { from_date:    filters.from_date }),
            ...(filters.to_date      && { to_date:      filters.to_date }),
            ...(isFacilityScoped && { facility_id: resolvedFacilityIds.join(',') }),
            ...(resolvedClauseCodes.length && { clause_code: resolvedClauseCodes.join(',') }),
        };
        try {
            const results = await Promise.allSettled([
                QoCService.getComparisonReport(f),
                QoCService.getComplianceTrends(isFacilityScoped ? resolvedFacilityIds.join(',') : null, 12),
                QoCService.getPerformanceDistribution(f),
                QoCService.getAuditSchedules(f),
                QoCService.getClauseScoresAcrossFacilities({ ...f, ...(resolvedClauseCodes.length && { clause_code: resolvedClauseCodes.join(',') }) }),
                QoCService.getKpiVsActual(kf),
                QoCService.getQualityIndex(kf),
                QoCService.getRegionalPerformance ? QoCService.getRegionalPerformance(kf).catch(()=>({data:{data:[]}})) : Promise.resolve({data:{data:[]}}),
                QoCService.getCustomerSurveys({ ...f, period_label: filters.period_label }),
            ]);
            const ok = (r, key='data') => r.status==='fulfilled' ? r.value.data?.[key]||[] : [];
           const comp           = ok(results[0]);
            const pendingReview  = results[0].status==='fulfilled' ? results[0].value.data?.pending_review||[] : [];
            const pendingCount   = pendingReview.reduce((sum,r)=>sum+parseInt(r.count||0),0);
            const draftCount     = results[0].status==='fulfilled' ? results[0].value.data?.draft_count||0 : 0;
            const trends    = ok(results[1]);
            const dist      = ok(results[2]);
            const schedRes  = results[3].status==='fulfilled' ? results[3].value.data||{} : {};
            const clauseRaw = ok(results[4]);
            const kpi       = results[5].status==='fulfilled' ? results[5].value.data?.data||{} : {};
            const qi        = ok(results[6]);
            const surveyRes = results[8]?.status==='fulfilled' ? results[8].value.data||{} : {};

            // Segregate by standard — filter BEFORE setting state
            // so all subsequent computations (subData, meData) are scoped correctly
            let clauseRawFiltered = clauseRaw;
            // 1. Filter by standard
            if (filters.standard_id) {
                const stdCodes = new Set(allClauses.filter(c => c.standard_id === filters.standard_id).map(c => c.code));
                clauseRawFiltered = clauseRawFiltered.filter(r => stdCodes.has(r.clause_code));
            }
            // 2. Filter by selected clauses (multi) — server-side clause_code filter
            // already narrowed this for getClauseScoresAcrossFacilities above, but
            // this client-side pass stays as a safety net (e.g. if the backend
            // silently ignored an unrecognized param) and costs nothing extra.
            if (resolvedClauseCodes.length) {
                clauseRawFiltered = clauseRawFiltered.filter(r => resolvedClauseCodes.includes(r.clause_code));
            }
            // 3. Facility scope (region + level + explicit facility selection,
            // already resolved above) — same reasoning: backend already scoped
            // this via facility_id, this is the client-side safety net.
            if (isFacilityScoped) {
                const idSet = new Set(resolvedFacilityIds.map(String));
                clauseRawFiltered = clauseRawFiltered.filter(r => idSet.has(String(r.facility_id)));
            }

            const today     = moment().startOf('day');
            const allScheds = schedRes.data || [];
            const overdueScheds = allScheds.filter(s => s.status!=='completed' && s.status!=='cancelled' && moment(s.scheduled_date).isBefore(today));
            // Facility scope safety net for comparison data too
            let compFiltered = comp;
            if (isFacilityScoped) {
                const idSet = new Set(resolvedFacilityIds.map(String));
                compFiltered = compFiltered.filter(f => idSet.has(String(f.facility_id)));
            }
            const avg = compFiltered.length ? compFiltered.reduce((s,f)=>s+getFacilityScore(f, scoringMode),0)/compFiltered.length : 0;

            setOverview({
                avg_score:          parseFloat(avg.toFixed(1)),
                assessed_count:     compFiltered.filter(f=>f.assessment_count>0).length,
                high_performers:    compFiltered.filter(f=>getFacilityScore(f, scoringMode)>=90).length,
                needs_attention:    compFiltered.filter(f=>getFacilityScore(f, scoringMode)<70).length,
                overdue_count:      0, // removed
                overdue_scheds:     overdueScheds,
                schedule_total:     0, // removed
                schedule_completed: 0, // removed
                schedule_inprog:    allScheds.filter(s=>s.status==='in_progress').length,
                pending_review_count: pendingCount,
                draft_count: draftCount,
            });
            setTrend(trends);
            setComp(compFiltered.sort((a,b)=>getFacilityScore(b, scoringMode)-getFacilityScore(a, scoringMode)));
            setDist(dist);
            setSched({ data: allScheds, summary: schedRes.summary||{} });
            setClause(clauseRawFiltered);
            setKPI(kpi);
            setQI(qi);
            const regionMap = {};
            compFiltered.forEach(f => { const r = f.region || f.located_region || 'Unknown'; if (!regionMap[r]) regionMap[r] = { region: r, scores: [], facility_count: 0 }; regionMap[r].scores.push(getFacilityScore(f, scoringMode)); regionMap[r].facility_count++; });
            setRegional(Object.values(regionMap).map(r => ({ region: r.region, facility_count: r.facility_count, avg_score: r.scores.length ? (r.scores.reduce((a,b)=>a+b,0)/r.scores.length).toFixed(2) : '0' })));
            setSurveyData({
                surveys:     surveyRes.data||[],
                stats:       surveyRes.stats||{},
                by_facility: (surveyRes.data||[]).reduce((acc,s)=>{
                    if (!acc[s.facility_id]) acc[s.facility_id]={name:s.facility_name,ip:[],op:[]};
                    if (s.survey_type==='inpatient') acc[s.facility_id].ip.push(s);
                    else acc[s.facility_id].op.push(s);
                    return acc;
                },{}),
            });
            const sm = {};
            clauseRawFiltered.forEach(r => {
                if (!sm[r.clause_code]) sm[r.clause_code]={code:r.clause_code,title:r.clause_title,scores:[],stdId:allClauses.find(c=>c.code===r.clause_code)?.standard_id};
                sm[r.clause_code].scores.push(parseFloat(r.avg_score||r.total_score||0));
            });
            setSub(Object.values(sm).map(s=>({...s, avg:s.scores.length?s.scores.reduce((a,b)=>a+b,0)/s.scores.length:0, count:s.scores.length})).sort((a,b)=>a.avg-b.avg));
            setME(clauseRawFiltered.filter(r=>parseFloat(r.avg_score||r.total_score||0)<70).sort((a,b)=>parseFloat(a.avg_score||a.total_score||0)-parseFloat(b.avg_score||b.total_score||0)).slice(0,30));
        } catch(e) { console.error(e); }
        finally { setLoading(false); }
    };

    // sf(): generic multi-select setter. antd Select with mode="multiple"
    // already hands back the full new array on change, so this just assigns
    // it — plus a dependent cleanup: if region or level narrows and some
    // currently-selected facility_id(s) no longer match ANY of the newly
    // selected regions/levels, those facility ids drop out of the selection
    // too (extends the old single-select "clear facility if it doesn't match"
    // behavior to the multi-select case).
    const sf = (k,v) => setFilters(f => {
        const next = { ...f, [k]: v };
        if (k === 'region' && f.facility_id?.length) {
            next.facility_id = f.facility_id.filter(fid => {
                const fac = facilities.find(x => String(x.id) === String(fid));
                return fac && (!v?.length || v.includes(fac.located_region));
            });
        }
        if (k === 'level' && f.facility_id?.length) {
            next.facility_id = (next.facility_id || f.facility_id).filter(fid => {
                const fac = facilities.find(x => String(x.id) === String(fid));
                return fac && (!v?.length || v.includes(fac.site_type));
            });
        }
        return next;
    });

    // Map clicks / region-list clicks toggle a single region in/out of the
    // array, rather than replacing the whole selection (that's what made
    // single-select "click again to deselect" work before; this is the
    // multi-select equivalent). Passing null clears the whole region filter
    // (used by the map's "Clear region filter" button).
    const toggleRegion = (region) => {
        if (region === null) { sf('region', []); return; }
        const cur = filters.region || [];
        sf('region', cur.includes(region) ? cur.filter(r=>r!==region) : [...cur, region]);
    };

    const scrollTo = (id) => { const el=document.getElementById(id); if(el) el.scrollIntoView({behavior:'smooth',block:'start'}); };

    // ─── Chart options ────────────────────────────────────────────────────────
    const trendOpt = () => ({
        tooltip:{trigger:'axis',backgroundColor:'rgba(15,15,35,0.92)',textStyle:{color:'#fff'}},
        legend:{data:['Avg Score','Target 90%','Benchmark 85%'],textStyle:{fontSize:11},bottom:0},
        grid:{top:20,right:20,bottom:50,left:48},
        xAxis:{type:'category',data:trendData.map(t=>moment(t.month).format('MMM YY')),axisLabel:{fontSize:10,rotate:30}},
        yAxis:{type:'value',min:0,max:100,axisLabel:{formatter:v=>`${v}%`,fontSize:10}},
        series:[
            {name:'Avg Score',type:'line',smooth:true,symbolSize:5,
             data:trendData.map(t=>parseFloat(t.avg_compliance||0).toFixed(1)),
             lineStyle:{width:3,color:'#1890ff'},itemStyle:{color:'#1890ff'},
             areaStyle:{color:{type:'linear',x:0,y:0,x2:0,y2:1,colorStops:[{offset:0,color:'rgba(24,144,255,0.28)'},{offset:1,color:'rgba(24,144,255,0.02)'}]}}},
            {name:'Target 90%',type:'line',data:Array(trendData.length).fill(90),symbol:'none',lineStyle:{type:'dashed',color:'#52c41a',width:2},itemStyle:{color:'#52c41a'}},
            {name:'Benchmark 85%',type:'line',data:Array(trendData.length).fill(85),symbol:'none',lineStyle:{type:'dashed',color:'#faad14',width:2},itemStyle:{color:'#faad14'}},
        ]
    });
    const distPieOpt = () => ({
        tooltip:{trigger:'item',formatter:'{b}: {c} ({d}%)'},
        legend:{orient:'vertical',right:0,top:'center',textStyle:{fontSize:10}},
        series:[{type:'pie',radius:['42%','68%'],center:['42%','50%'],
            data:distData.filter(d=>d.facility_count>0).map(d=>({value:d.facility_count,name:d.range_label,itemStyle:{color:d.color}})),
            label:{show:true,formatter:'{b}\n{c}',fontSize:10},emphasis:{itemStyle:{shadowBlur:8}}}]
    });
    const facilityBarOpt = () => {
        const d=compData.slice(0,25);
        return {
            tooltip:{trigger:'axis'},
            grid:{top:10,right:60,bottom:30,left:160},
            xAxis:{type:'value',max:100,axisLabel:{formatter:v=>`${v}%`,fontSize:9}},
            yAxis:{type:'category',data:d.map(f=>(f.facility_name||'').slice(0,26)).reverse(),axisLabel:{fontSize:9}},
            series:[{type:'bar',barMaxWidth:14,
                data:d.map(f=>{const v=getFacilityScore(f, scoringMode);const r=getRating(v);return{value:parseFloat(v.toFixed(1)),itemStyle:{color:r.color,borderRadius:[0,4,4,0]}};}).reverse(),
                label:{show:true,position:'right',formatter:p=>`${p.value}%`,fontSize:9}}]
        };
    };
    const schedDonutOpt = () => {
        const today=moment().startOf('day'); const all=schedData.data;
        const counts={
            completed:   all.filter(s=>s.status==='completed').length,
            in_progress: all.filter(s=>s.status==='in_progress').length,
            scheduled:   all.filter(s=>s.status==='scheduled'&&!moment(s.scheduled_date).isBefore(today)).length,
            overdue:     all.filter(s=>s.status!=='completed'&&s.status!=='cancelled'&&moment(s.scheduled_date).isBefore(today)).length,
            cancelled:   all.filter(s=>s.status==='cancelled').length,
        };
        return {
            tooltip:{trigger:'item',formatter:'{b}: {c} ({d}%)'},
            legend:{bottom:0,textStyle:{fontSize:10}},
            series:[{type:'pie',radius:['45%','72%'],center:['50%','44%'],
                data:[
                    {value:counts.completed,  name:'Completed',  itemStyle:{color:'#52c41a'}},
                    {value:counts.in_progress,name:'In Progress',itemStyle:{color:'#faad14'}},
                    {value:counts.scheduled,  name:'Scheduled',  itemStyle:{color:'#1890ff'}},
                    {value:counts.overdue,    name:'Overdue',    itemStyle:{color:'#f5222d'}},
                    {value:counts.cancelled,  name:'Cancelled',  itemStyle:{color:'#bbb'}},
                ].filter(d=>d.value>0),
                label:{formatter:'{b}\n{c}',fontSize:10}}]
        };
    };
    const clauseRadarOpt = () => {
        if (!clauseData.length) return {};
        const cm={};
        clauseData.forEach(r=>{if(!cm[r.clause_code])cm[r.clause_code]=[];cm[r.clause_code].push(parseFloat(r.avg_score||r.total_score||0));});
        const cl=CLAUSES.map(c=>({code:c.code,avg:cm[c.code]?cm[c.code].reduce((a,b)=>a+b,0)/cm[c.code].length:0}));
        return {
            tooltip:{trigger:'item'},
            radar:{indicator:cl.map(c=>({name:c.code,max:100})),radius:'65%',center:['50%','52%'],axisName:{fontSize:11,fontWeight:700},splitNumber:5,splitArea:{areaStyle:{color:['#f8f9fa','#fff']}}},
            series:[{type:'radar',data:[{value:cl.map(c=>parseFloat(c.avg.toFixed(1))),name:'Avg Score',areaStyle:{color:'rgba(24,144,255,0.18)'},lineStyle:{color:'#1890ff',width:2},itemStyle:{color:'#1890ff'}}]}]
        };
    };
    const clauseBarOpt = () => {
        const cm={};
        clauseData.forEach(r=>{if(!cm[r.clause_code])cm[r.clause_code]=[];cm[r.clause_code].push(parseFloat(r.avg_score||r.total_score||0));});
        const data=CLAUSES.map(c=>{const scores=cm[c.code]||[];const avg=scores.length?scores.reduce((a,b)=>a+b,0)/scores.length:0;return{code:c.code,avg:parseFloat(avg.toFixed(1)),color:c.color};}).sort((a,b)=>a.avg-b.avg);
        return {
            tooltip:{trigger:'axis'},
            grid:{top:10,right:60,bottom:30,left:55},
            xAxis:{type:'value',max:100,axisLabel:{formatter:v=>`${v}%`,fontSize:9}},
            yAxis:{type:'category',data:data.map(d=>d.code),axisLabel:{fontSize:11,fontWeight:700}},
            series:[{type:'bar',barMaxWidth:18,data:data.map(d=>({value:d.avg,itemStyle:{color:d.color,borderRadius:[0,5,5,0]}})),label:{show:true,position:'right',formatter:p=>`${p.value}%`,fontSize:10}}]
        };
    };
    const gaugeOpt = (target, actual) => ({
        series:[{type:'gauge',radius:'90%',
            axisLine:{lineStyle:{width:14,color:[[Math.min(actual/100,1),'#1890ff'],[1,'#f0f0f0']]}},
            pointer:{show:false},axisTick:{show:false},splitLine:{show:false},axisLabel:{show:false},
            progress:{show:true,width:14},
            detail:{valueAnimation:true,formatter:v=>`${v}%\n▸${target}%`,color:actual>=target?'#52c41a':'#f5222d',fontSize:11,fontWeight:700,offsetCenter:[0,0]},
            data:[{value:parseFloat(actual.toFixed(1))}]
        }]
    });
    // qiFiltered now reuses resolvedFacilityIds — the SAME facility scope the
    // backend call already used to fetch qiData in the first place — rather
    // than re-deriving a filter from raw filters.region/level/facility_id.
    // This is also what makes clause-scoped Quality Index numbers show up
    // here correctly: qiData itself is already the clause-recalculated set
    // (see kf.clause_code above), this just narrows it to the facility scope.
    const qiFiltered = isFacilityScoped
        ? qiData.filter(q => resolvedFacilityIds.some(id => String(id) === String(q.facility_id)))
        : qiData;
    const qiBarOpt = () => {
        const sorted=[...qiFiltered].sort((a,b)=>qiSortAsc?parseFloat(a.quality_index)-parseFloat(b.quality_index):parseFloat(b.quality_index)-parseFloat(a.quality_index)).slice(0,qiPageSize);
        return {
            tooltip:{trigger:'axis'},
            legend:{data:['QI Score','QoC Score'],bottom:0,textStyle:{fontSize:11}},
            grid:{top:10,right:20,bottom:40,left:140},
            xAxis:{type:'value',max:100,axisLabel:{formatter:v=>`${v}%`,fontSize:9}},
            yAxis:{type:'category',data:sorted.map(q=>(q.facility_name||'').slice(0,22)).reverse(),axisLabel:{fontSize:9,color:'#374151'}},
            series:[
                {name:'QI Score',type:'bar',barMaxWidth:10,
                 data:sorted.map(q=>{const r=getRating(parseFloat(q.quality_index||0));return{value:parseFloat(q.quality_index||0).toFixed(1),itemStyle:{color:r.color,borderRadius:[0,4,4,0]},label:{show:true,position:'right',formatter:p=>p.value+'%',color:r.color,fontWeight:700,fontSize:11}};}).reverse(),
                 label:{show:true,position:'right',fontSize:9}},
                {name:'QoC Score',type:'bar',barMaxWidth:10,
                 data:sorted.map(q=>({value:parseFloat(q.qoc_score||0).toFixed(1),itemStyle:{color:'rgba(24,144,255,0.28)',borderRadius:[0,4,4,0]}})).reverse()},
            ]
        };
    };

    const today = moment().startOf('day');
    const NAVLINKS = [
        {id:'sec-overview',label:'Overview',icon:'📊'},
        {id:'sec-clauses',label:filters.standard_id&&standards.find(s=>s.id===filters.standard_id)?`${standards.find(s=>s.id===filters.standard_id).name?.slice(0,8)} Clauses`:'Clauses',icon:'🔵'},
        {id:'sec-mes',label:'Low MEs',icon:'⚠'},
        {id:'sec-qindex',label:'Quality Index',icon:'⭐'},
        {id:'sec-kpis',label:'KPI vs Actuals',icon:'🎯'},
        {id:'sec-surveys',label:'Survey Dashboard',icon:'📝'},
        {id:'sec-comparison',label:'Period Comparison',icon:'🔄'},
    ];
    const goToAwards = () => window.open('/qoc/awards', '_blank');
    const surveyScoreColor = s => s>=75?'#52c41a':s>=50?'#fa8c16':'#f5222d';
    const surveyBarOpt = () => {
        const byFac=Object.values(surveyData.by_facility); if(!byFac.length) return {};
        const names=byFac.map(f=>(f.name||'').slice(0,22));
        const ipAvgs=byFac.map(f=>!f.ip.length?null:parseFloat((f.ip.reduce((s,x)=>s+parseFloat(x.overall_score||0),0)/f.ip.length).toFixed(1)));
        const opAvgs=byFac.map(f=>!f.op.length?null:parseFloat((f.op.reduce((s,x)=>s+parseFloat(x.overall_score||0),0)/f.op.length).toFixed(1)));
        return {
            tooltip:{trigger:'axis'},legend:{data:['Inpatient Avg','Outpatient Avg'],bottom:0,textStyle:{fontSize:11}},
            grid:{top:10,right:60,bottom:40,left:160},
            xAxis:{type:'value',max:100,axisLabel:{formatter:v=>`${v}%`,fontSize:9}},
            yAxis:{type:'category',data:names,axisLabel:{fontSize:10}},
            series:[
                {name:'Inpatient Avg',type:'bar',barMaxWidth:12,data:ipAvgs.map(v=>v===null?{value:0,itemStyle:{opacity:0}}:{value:v,itemStyle:{color:'#0050b3',borderRadius:[0,4,4,0]}}),label:{show:true,position:'right',fontSize:9,formatter:p=>p.value>0?`${p.value}%`:''}},
                {name:'Outpatient Avg',type:'bar',barMaxWidth:12,data:opAvgs.map(v=>v===null?{value:0,itemStyle:{opacity:0}}:{value:v,itemStyle:{color:'#006837',borderRadius:[0,4,4,0]}}),label:{show:true,position:'right',fontSize:9,formatter:p=>p.value>0?`${p.value}%`:''}},
            ],
        };
    };
    const surveyDistOpt = (type) => {
        const surveys=surveyData.surveys.filter(s=>s.survey_type===type); if(!surveys.length) return {};
        const buckets={'≥75 Good':0,'50–74 Fair':0,'<50 Poor':0};
        surveys.forEach(s=>{const v=parseFloat(s.overall_score||0);if(v>=75)buckets['≥75 Good']++;else if(v>=50)buckets['50–74 Fair']++;else buckets['<50 Poor']++;});
        const colors={'≥75 Good':'#52c41a','50–74 Fair':'#fa8c16','<50 Poor':'#f5222d'};
        return {
            tooltip:{trigger:'item',formatter:'{b}: {c} ({d}%)'},
            series:[{type:'pie',radius:['42%','68%'],center:['50%','48%'],
                data:Object.entries(buckets).filter(([,v])=>v>0).map(([k,v])=>({name:k,value:v,itemStyle:{color:colors[k]}})),
                label:{formatter:'{b}\n{c}',fontSize:10}}],
        };
    };
    const surveyTimelineOpt = () => {
        const sorted=[...surveyData.surveys].sort((a,b)=>moment(a.survey_date).unix()-moment(b.survey_date).unix());
        const ip=sorted.filter(s=>s.survey_type==='inpatient'); const op=sorted.filter(s=>s.survey_type==='outpatient');
        return {
            tooltip:{trigger:'axis'},legend:{data:['Inpatient','Outpatient'],bottom:0,textStyle:{fontSize:11}},
            grid:{top:10,right:20,bottom:40,left:48},
            xAxis:{type:'category',data:[...new Set(sorted.map(s=>moment(s.survey_date).format('DD MMM YY')))],axisLabel:{fontSize:9,rotate:30}},
            yAxis:{type:'value',min:0,max:100,axisLabel:{formatter:v=>`${v}%`,fontSize:9}},
            series:[
                {name:'Inpatient',type:'scatter',symbolSize:10,data:ip.map(s=>[moment(s.survey_date).format('DD MMM YY'),parseFloat(s.overall_score||0).toFixed(1)]),itemStyle:{color:'#0050b3'}},
                {name:'Outpatient',type:'scatter',symbolSize:10,data:op.map(s=>[moment(s.survey_date).format('DD MMM YY'),parseFloat(s.overall_score||0).toFixed(1)]),itemStyle:{color:'#006837'}},
            ],
        };
    };

    // Facility dropdown options are restricted to the currently-selected
    // region(s) and level(s) — this is the literal "facilities shown based on
    // level and region selected" requirement. Computed once here so both the
    // dropdown and any other place needing "facilities matching current
    // region/level" can reuse it.
    const facilitiesForDropdown = facilities.filter(f =>
        (!filters.region?.length || filters.region.includes(f.located_region)) &&
        (!filters.level?.length  || filters.level.includes(f.site_type))
    );

    const hasActiveFilters = (filters.facility_id?.length||filters.level?.length||filters.clause_id?.length||filters.period_label||filters.region?.length);

    return (
        <>
        <div className="page-content" ref={dashboardRef} data-pdf-root="true">

            {/* ── Dark header ── */}
            <div className="page-head uc d-flex" style={{background:'linear-gradient(135deg,#0f172a,#1e293b)',padding:'14px 24px',marginBottom:0}}>
                <div className="my-auto" style={{display:'flex',alignItems:'center',gap:12}}>
                    <BarChartOutlined style={{fontSize:22,color:'#60a5fa'}}/>
                    <div>
                        <h2 style={{margin:0,color:'#fff',fontSize:18,fontWeight:800}}>Executive Dashboard</h2>
                        <div style={{fontSize:11,color:'rgba(255,255,255,0.45)'}}>Eswatini Quality of Care — Health Standards Management</div>
                    </div>
                </div>
                <div className="my-auto ml-auto" style={{display:'flex',alignItems:'center',gap:12}}>
                    <span style={{fontSize:11,color:'rgba(255,255,255,0.35)'}}>{moment().format('DD MMM YYYY HH:mm')}</span>
                    
                    {/* Download PDF temporarily disabled */}
                </div>
            </div>

            {/* ── STICKY FILTER + NAV BAR ── */}
            <div style={{position:'sticky',top:0,zIndex:100,background:'#fff',borderBottom:'2px solid #e8f0fe',boxShadow:'0 2px 12px rgba(0,0,0,0.08)'}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'6px 16px'}}>
                    <button onClick={()=>setFiltersOpen(o=>!o)} style={{display:'flex',alignItems:'center',gap:6,background:'#f0f5ff',border:'1px solid #c7d7f9',borderRadius:6,padding:'4px 12px',cursor:'pointer',fontSize:12,fontWeight:600,color:'#1f3864'}}>
                        <span>{filtersOpen?'▲':'▼'}</span> Filters {filtersOpen?'(hide)':'(show)'}
                    </button>
                </div>
                <div style={{maxHeight:filtersOpen?'200px':'0',overflow:'hidden',transition:'max-height 0.25s ease'}}>
                <div style={{display:'flex',alignItems:'center',gap:8,padding:'6px 16px',flexWrap:'wrap'}}>
                    {/* Order: Level, Region, Standard, Facility, Clause, Period —
                        Level and Region come first since the Facility dropdown's
                        own options depend on them (facilities shown = whatever
                        matches the level(s)/region(s) currently selected). */}
                    <Select
                        mode="multiple"
                        allowClear
                        size="small"
                        placeholder="All Levels"
                        style={{ minWidth:160 }}
                        maxTagCount={1}
                        value={filters.level}
                        onChange={v=>sf('level',v)}
                    >
                        {LEVELS.map(l=><Option key={l} value={l}>{l}</Option>)}
                    </Select>
                    <Select
                        mode="multiple"
                        allowClear
                        size="small"
                        placeholder="All Regions"
                        style={{ minWidth:160 }}
                        maxTagCount={1}
                        value={filters.region}
                        onChange={v=>sf('region',v)}
                    >
                        {REGIONS.map(r=><Option key={r} value={r}>{r}</Option>)}
                    </Select>
                    <Select allowClear size="small" placeholder="All Standards" style={{width:160}} value={filters.standard_id} onChange={v => setFilters(f => ({ ...f, standard_id: v||null, clause_id: [] }))}>
                        {standards.filter(s => s.category !== 'Health Awards').map(s => (
                            <Option key={s.id} value={s.id}>
                                <span style={{display:'inline-flex',alignItems:'center',gap:6}}>
                                    <span style={{width:10,height:10,borderRadius:'50%',background:s.accent||'#1890ff',display:'inline-block',flexShrink:0}}/>
                                    {s.name}
                                </span>
                            </Option>
                        ))}
                    </Select>
                    <Select
                        mode="multiple"
                        allowClear
                        size="small"
                        placeholder="All Facilities"
                        style={{ minWidth:200 }}
                        maxTagCount={1}
                        showSearch
                        optionFilterProp="children"
                        value={filters.facility_id}
                        onChange={v=>sf('facility_id',v)}
                    >
                        {facilitiesForDropdown.map(f=><Option key={f.id} value={f.id}>{f.business_name}</Option>)}
                    </Select>
                    {/* Clause — grouped by standard when all standards showing, multi-select */}
                    <Select
                        mode="multiple"
                        allowClear
                        size="small"
                        placeholder="All Clauses"
                        style={{ minWidth:220 }}
                        maxTagCount={1}
                        value={filters.clause_id}
                        onChange={v=>sf('clause_id',v)}
                    >
                        {filters.standard_id
                            ? allClauses.filter(c=>c.standard_id===filters.standard_id).map(c => {
                                const std = standards.find(s=>s.id===c.standard_id);
                                return <Option key={c.id} value={c.id}>
                                    <span style={{fontWeight:700,marginRight:4,color:std?.accent||'#1890ff'}}>{c.code}</span>
                                    <span style={{fontSize:11,color:'#888'}}>{(c.title||'').slice(0,22)}</span>
                                </Option>;
                            })
                            : standards.map(std => {
                                const sc = allClauses.filter(c=>c.standard_id===std.id);
                                if (!sc.length) return null;
                                return (
                                    <OptGroup key={std.id} label={<span style={{fontWeight:700,color:std.accent||'#555'}}>{std.name}</span>}>
                                        {sc.map(c=>(
                                            <Option key={c.id} value={c.id}>
                                                <span style={{fontWeight:700,marginRight:4,color:std.accent||'#1890ff'}}>{c.code}</span>
                                                <span style={{fontSize:11,color:'#888'}}>{(c.title||'').slice(0,22)}</span>
                                            </Option>
                                        ))}
                                    </OptGroup>
                                );
                            }).filter(Boolean)
                        }
                    </Select>
                    <div style={{display:'flex',alignItems:'center',gap:6,background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:6,padding:'3px 10px',fontSize:12,color:'#64748b'}}>📅 {filters.from_date||'—'} → {filters.to_date||'—'}</div>{false && <RangePicker size="small" style={{width:220}} value={dateRange}
                        onChange={v=>{setDateRange(v||[moment().subtract(12,'months'),moment()]);sf('from_date',(v?.[0]||moment().subtract(12,'months')).format('YYYY-MM-DD'));sf('to_date',(v?.[1]||moment()).format('YYYY-MM-DD'));}}/> }
                    <Select allowClear size="small" placeholder="Assessment Period" style={{width:180}} value={filters.period_label}
                        onChange={v=>{sf('period_label',v);const pd=periods.find(p=>p.label===v);if(pd){setDateRange([moment(pd.period_start),moment(pd.period_end)]);sf('from_date',pd.period_start);sf('to_date',pd.period_end);}}}>
                        {periods.filter(p=>!p.label.startsWith('ENHEA')).map(p=><Option key={p.label} value={p.label}>{p.is_current==1&&'★ '}{p.label}</Option>)}
                    </Select>
                    <Button size="small" icon={<ReloadOutlined/>} onClick={load} loading={loading}>Refresh</Button>
                    {hasActiveFilters&&(
                        <Button size="small" type="link" style={{color:'#f5222d',padding:0}} onClick={()=>{const cur=periods.find(p=>parseInt(p.is_current)===1);setFilters(f=>({...f,facility_id:[],level:[],clause_id:[],standard_id:'',period_label:cur?.label||'',region:lockedRegion?[lockedRegion]:[],from_date:cur?.period_start||f.from_date,to_date:cur?.period_end||f.to_date}));}}>Clear filters ×</Button>
                    )}
                </div>
                </div>{/* end collapsible filters */}
                <div style={{padding:'0 20px',display:'flex',gap:0,overflowX:'auto'}}>
                    {NAVLINKS.map(n=>(
                        <div key={n.id} onClick={()=>scrollTo(n.id)}
                            style={{padding:'8px 14px',cursor:'pointer',fontSize:12,fontWeight:600,color:'#555',whiteSpace:'nowrap',borderBottom:'3px solid transparent',transition:'all 0.15s',userSelect:'none',display:'flex',alignItems:'center',gap:4}}
                            onMouseEnter={e=>{e.currentTarget.style.color='#1890ff';e.currentTarget.style.borderBottomColor='#1890ff';}}
                            onMouseLeave={e=>{e.currentTarget.style.color='#555';e.currentTarget.style.borderBottomColor='transparent';}}>
                            {n.icon} {n.label}
                        </div>
                    ))}
                </div>
            </div>

            {/* Active standard banner */}
            {filters.standard_id && standards.find(s=>s.id===filters.standard_id) && (() => {
                const std = standards.find(s => s.id === filters.standard_id);
                return (
                    <div style={{ padding:'6px 20px', background:`${std.accent}12`,
                        borderBottom:`2px solid ${std.accent}40`,
                        display:'flex', alignItems:'center', gap:10, fontSize:12 }}>
                        <span style={{ width:12, height:12, borderRadius:'50%', background:std.accent, display:'inline-block' }}/>
                        <span style={{ fontWeight:700, color:std.accent }}>Showing: {std.name}</span>
                        <span style={{ color:'#888' }}>— all charts and scores filtered to this standard's clauses only. Scores are NOT mixed with other standards.</span>
                        <Tag style={{ background:std.accent, color:'#fff', border:'none', fontSize:10, marginLeft:'auto' }}>
                            {allClauses.filter(c=>c.standard_id===std.id).length} clauses
                        </Tag>
                    </div>
                );
            })()}
            {/* ── SCROLLABLE CONTENT ── */}
            <div style={{background:'#f4f6fb',padding:'0 24px 40px'}}>
                <Spin spinning={loading}>

                {/* ════ SECTION 1 — OVERVIEW ════ */}
                <SectionTitle id="sec-overview" icon={<BarChartOutlined/>} title="Overview" subtitle="National QoC performance at a glance" color="#1890ff">
                

                {false && overview.avg_score > 0 && (()=>{
                    const r=getRating(overview.avg_score);
                    return (
                        <div style={{background:`linear-gradient(135deg,${r.color}14,${r.color}06)`,border:`2px solid ${r.color}40`,borderRadius:14,padding:'20px 28px',marginBottom:16,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:16}}>
                            <div>
                                <div style={{fontSize:11,color:'#888',fontWeight:700,letterSpacing:1,marginBottom:4}}>{filters.region?.length ? filters.region.join(', ').toUpperCase() + ' AVG SCORE' : filters.level?.length ? filters.level.join(', ').toUpperCase() + ' AVG' : 'NATIONAL AVERAGE RATING'}</div>
                                <div style={{fontSize:52,fontWeight:900,color:r.color,lineHeight:1}}>{overview.avg_score}%</div>
                                <div style={{marginTop:8}}><Stars count={r.stars} size={22}/></div>
                            </div>
                            <div style={{textAlign:'center'}}>
                                <Progress type="circle" percent={Math.min(100,overview.avg_score)} width={110} strokeColor={r.color} format={()=><span style={{fontWeight:900,fontSize:18,color:r.color}}>{overview.avg_score}%</span>}/>
                                <div style={{fontWeight:800,fontSize:16,color:r.color,marginTop:8}}>{r.label}</div>
                            </div>
                            <div>{RATING.map(r2=>(
                                <div key={r2.label} style={{display:'flex',alignItems:'center',gap:8,marginBottom:5,opacity:overview.avg_score>=r2.min?1:0.3}}>
                                    <Stars count={r2.stars} size={11}/>
                                    <span style={{fontSize:11,color:r2.color,fontWeight:700,minWidth:140}}>{r2.label}</span>
                                    <span style={{fontSize:10,color:'#aaa'}}>{r2.min}–{r2.max}%</span>
                                </div>
                            ))}</div>
                        </div>
                    );
                })()}

                <Row gutter={16} style={{marginBottom:8}}>
                    <Col span={12}>
                        {false && <XCard title={<span style={{color:'#389e0d'}}>🏆 Top 10 Facilities</span>} filename="Top_10_Facilities">
                            {compData.slice(0,10).map((f,i)=>{
                                const r=getRating(getFacilityScore(f, scoringMode));
                                return (
                                    <div key={f.facility_id||i} style={{display:'flex',alignItems:'center',gap:8,padding:'5px 0',borderBottom:'1px solid #f5f5f5'}}>
                                        <span style={{width:22,fontSize:10,color:'#aaa',flexShrink:0}}>#{i+1}</span>
                                        <div style={{flex:1,fontSize:12,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.facility_name}</div>
                                        <Tag style={{background:r.color,color:'#fff',border:'none',fontSize:10,fontWeight:700}}>{getFacilityScore(f, scoringMode).toFixed(1)}%</Tag>
                                        <Stars count={r.stars} size={10}/>
                                        <span onClick={()=>setReportFacility({id:f.facility_id,business_name:f.facility_name,site_type:f.facility_level,located_region:f.region})}
                                            style={{cursor:'pointer',color:'#f5222d',fontSize:10,flexShrink:0,padding:'1px 6px',border:'1px solid #ffccc7',borderRadius:4}}>PDF</span>
                                    </div>
                                );
                            })}
                        </XCard>}
                    </Col>
                    <Col span={12}>
                        {false && <XCard title={<span style={{color:'#f5222d'}}>⚠ Needs Attention (Bottom 10)</span>} filename="Needs_Attention">
                            {[...compData].reverse().slice(0,10).map((f,i)=>{
                                const r=getRating(getFacilityScore(f, scoringMode));
                                return (
                                    <div key={f.facility_id||i} style={{display:'flex',alignItems:'center',gap:8,padding:'5px 0',borderBottom:'1px solid #f5f5f5'}}>
                                        <div style={{flex:1,fontSize:12,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.facility_name}</div>
                                        <span style={{fontSize:10,color:'#aaa',flexShrink:0}}>{f.facility_level||'—'}</span>
                                        <Tag style={{background:r.color,color:'#fff',border:'none',fontSize:10,fontWeight:700}}>{getFacilityScore(f, scoringMode).toFixed(1)}%</Tag>
                                        <span onClick={()=>setReportFacility({id:f.facility_id,business_name:f.facility_name,site_type:f.facility_level,located_region:f.region})}
                                            style={{cursor:'pointer',color:'#f5222d',fontSize:10,flexShrink:0,padding:'1px 6px',border:'1px solid #ffccc7',borderRadius:4}}>PDF</span>
                                    </div>
                                );
                            })}
                        </XCard>}
                    </Col>
                </Row>

                {/* Regional Performance Map + KPI Cards */}
                <div style={{display:'flex',gap:16,alignItems:'flex-start',marginTop:24,marginBottom:8}}>
                    <div style={{flex:'0 0 78%'}}>
                        {filters.region?.length>0 && <div onClick={()=>toggleRegion(null)} style={{fontSize:11,color:'#1d4ed8',fontWeight:700,marginBottom:8,padding:'3px 10px',background:'#eff6ff',borderRadius:6,display:'inline-block',cursor:'pointer'}}>x Clear filter: {filters.region.join(', ')}</div>}
                        <XCard title="Regional Performance Map" filename="Eswatini_Regional_Map">
                            <EswatiniMap regionalData={regionalData} filters={filters} toggleRegion={toggleRegion} />
                        </XCard>
                    </div>
                    <div style={{flex:'0 0 20%',display:'flex',flexDirection:'column',gap:10,paddingTop:8}}>
                        {overview && [{label:'Avg Score',value:overview.avg_score+'%',color:'#1890ff'},{label:'Facilities Assessed',value:overview.assessed_count,color:'#16a34a'},{label:'High Performers',value:overview.high_performers,color:'#7c3aed'},{label:'Needs Attention',value:overview.needs_attention,color:'#dc2626'},{label:'Pending Approval',value:overview.pending_count,color:'#d97706'}].map(k=>( <div key={k.label} style={{background:'#fff',borderRadius:10,padding:'12px 14px',boxShadow:'0 1px 4px rgba(0,0,0,0.08)',borderLeft:'4px solid '+k.color}}><div style={{fontSize:10,color:'#888',fontWeight:600,textTransform:'uppercase',letterSpacing:0.8}}>{k.label}</div><div style={{fontSize:22,fontWeight:800,color:k.color,lineHeight:1.2,marginTop:3}}>{k.value}</div></div> ))}
                    </div>
                </div>

                {/* ════ SECTION 2 — TREND & DISTRIBUTION ════ */}
                {false && <><SectionTitle id="sec-trend" icon={<LineChartOutlined/>} title="Compliance Trend & Distribution" subtitle="12-month national trend with targets, and facility score distribution" color="#0ea5e9"/>
                <Row gutter={16} style={{marginBottom:16}}>
                    <Col span={16}>
                        <XCard title="National Compliance Trend (12 months)" filename="National_Compliance_Trend">
                            {trendData.length ? <ReactECharts option={trendOpt()} style={{height:280}}/> : <Empty description="No trend data"/>}
                        </XCard>
                    </Col>
                    <Col span={8}>
                        <XCard title="Facility Distribution by Score Range" filename="Score_Distribution">
                            {distData.some(d=>d.facility_count>0) ? <ReactECharts option={distPieOpt()} style={{height:280}}/> : <Empty description="No data"/>}
                        </XCard>
                    </Col>
                </Row>
                <XCard title="Facility Score Comparison — Top 25" filename="Facility_Score_Comparison" style={{marginBottom:16}}>
                    {compData.length ? <ReactECharts option={facilityBarOpt()} style={{height:Math.max(300,Math.min(compData.length,25)*18+60)}}/> : <Empty description="No facility assessment data"/>}
                </XCard>

                {/* ════ SECTION 3 — SCHEDULES ════ */}
                </>
                }
                {false && <><SectionTitle id="sec-schedules" icon={<CalendarOutlined/>} title="Audit Schedules" subtitle="Overdue = scheduled date has passed and assessment not completed" color="#f59e0b"/>
                <Row gutter={[12,12]} style={{marginBottom:16}}>
                    {[
                        {label:'Total Schedules',               value:schedData.data.length,                                                                                          color:'#1890ff',icon:'📅'},
                        {label:'Completed',                     value:schedData.data.filter(s=>s.status==='completed').length,                                                        color:'#52c41a',icon:'✅'},
                        {label:'In Progress',                   value:schedData.data.filter(s=>s.status==='in_progress').length,                                                      color:'#faad14',icon:'🔄'},
                        {label:'Upcoming (not yet due)',         value:schedData.data.filter(s=>s.status==='scheduled'&&!moment(s.scheduled_date).isBefore(today)).length,             color:'#06b6d4',icon:'📆'},
                        {label:'Overdue (past date, not done)', value:overview.overdue_count||0, sub:'Based on scheduled_date vs today',                                              color:'#f5222d',icon:'⏰'},
                        {label:'Cancelled',                     value:schedData.data.filter(s=>s.status==='cancelled').length,                                                        color:'#999',   icon:'✗'},
                    ].map(k=>(<Col key={k.label} xs={12} sm={8} md={4}><KpiCard {...k}/></Col>))}
                </Row>
                <Row gutter={16} style={{marginBottom:16}}>
                    <Col span={8}>
                        <XCard title="Schedule Status Breakdown" filename="Schedule_Status">
                            {schedData.data.length ? <ReactECharts option={schedDonutOpt()} style={{height:280}}/> : <Empty description="No schedules found"/>}
                        </XCard>
                    </Col>
                    <Col span={16}>
                        <XCard title={<span><WarningFilled style={{color:'#f5222d',marginRight:6}}/>Overdue Schedules ({overview.overdue_count||0})</span>} filename="Overdue_Schedules">
                            <Table size="small"
                                dataSource={schedData.data.filter(s=>s.status!=='completed'&&s.status!=='cancelled'&&moment(s.scheduled_date).isBefore(today))}
                                rowKey="id" pagination={{pageSize:8,size:'small'}}
                                columns={[
                                    {title:'Facility',dataIndex:'facility_name',ellipsis:true,width:180},
                                    {title:'Clause',dataIndex:'clause_code',width:70,render:c=>c?<Tag color="blue" style={{fontSize:10}}>{c}</Tag>:<Tag>Full</Tag>},
                                    {title:'Sched. Date',dataIndex:'scheduled_date',width:100,render:d=><span style={{color:'#f5222d',fontWeight:700}}>{moment(d).format('DD MMM YY')}</span>},
                                    {title:'Days Overdue',width:100,render:(_,r)=>{const days=today.diff(moment(r.scheduled_date),'days');return <Tag color="red" style={{fontSize:10}}>{days}d overdue</Tag>;}},
                                    {title:'Lead Assessor',dataIndex:'lead_assessor_name',ellipsis:true,width:130},
                                    {title:'Status',dataIndex:'status',width:100,render:s=>{const cfg=SCHED_STATUS[s]||SCHED_STATUS.scheduled;return <Tag color={cfg.color} style={{fontSize:10}}>{cfg.label}</Tag>;}},
                                ]}/>
                        </XCard>
                    </Col>
                </Row>
                <XCard title="All Schedules" filename="All_Schedules" style={{marginBottom:16}}>
                    <Table size="small" dataSource={schedData.data} rowKey="id" pagination={{pageSize:12,size:'small'}}
                        columns={[
                            {title:'Facility',dataIndex:'facility_name',ellipsis:true,width:200},
                            {title:'Clause',dataIndex:'clause_code',width:70,render:c=>c?<Tag color="blue" style={{fontSize:10}}>{c}</Tag>:<Tag>Full</Tag>},
                            {title:'Scheduled Date',dataIndex:'scheduled_date',width:115,render:(d,r)=>{
                                const isOverdue=r.status!=='completed'&&r.status!=='cancelled'&&moment(d).isBefore(today);
                                return <span style={{color:isOverdue?'#f5222d':'inherit',fontWeight:isOverdue?700:400}}>{moment(d).format('DD MMM YY')}{isOverdue&&<Tag color="red" style={{marginLeft:4,fontSize:9}}>OVERDUE</Tag>}</span>;}},
                            {title:'Lead Assessor',dataIndex:'lead_assessor_name',ellipsis:true,width:140},
                            {title:'Status',dataIndex:'status',width:110,render:s=>{const cfg=SCHED_STATUS[s]||SCHED_STATUS.scheduled;return <Tag color={cfg.color} style={{fontSize:10}}>{cfg.label}</Tag>;}},
                            {title:'Assessment Score',dataIndex:'assessment_score',width:110,align:'center',render:v=>v?<span style={{fontWeight:700}}>{parseFloat(v).toFixed(1)}%</span>:'—'},
                        ]}/>
                </XCard>
{/* Empty state when standard filter returns no data */}
{filters.standard_id && clauseData.length === 0 && !loading && (
    <Alert
        style={{marginBottom:16}}
        message={`No assessment data found for ${standards.find(s=>s.id===filters.standard_id)?.name || 'selected standard'}`}
        description="There are no approved assessments for this standard in the selected date range."
        type="info" showIcon/>
)}
                {/* ════ SECTION 4 — CLAUSES grouped by standard (scores never mixed) ════ */}
                </>
                }
                </SectionTitle>{/* end overview */}<SectionTitle id="sec-clauses" icon={<PieChartOutlined/>}
                    title={filters.standard_id && standards.find(s=>s.id===filters.standard_id)
                        ? `${standards.find(s=>s.id===filters.standard_id).name} — Clause Performance`
                        : 'Clause Performance by Standard'}
                    subtitle={filters.standard_id
                        ? `Showing ${standards.find(s=>s.id===filters.standard_id)?.name} clauses only`
                        : 'Each standard shown separately — scores are never mixed across standards'}
                    color="#8b5cf6">
                {(() => {
                    // Build the full facility list once (not capped) so pagination has something to page through
                    const heatmapClausesList = (() => {
                        let cls = filters.standard_id
                            ? allClauses.filter(c => c.standard_id === filters.standard_id)
                            : allClauses.filter(c => c.code !== 'PROG' && !c.code.startsWith('ENHEA'));
                        if (resolvedClauseCodes.length) cls = cls.filter(c => resolvedClauseCodes.includes(c.code));
                        return cls;
                    })();
                    const allFacRows = Object.entries(clauseData.reduce((acc,r)=>{
                        if(!acc[r.facility_id]) acc[r.facility_id]={name:r.facility_name||'—',scores:{}};
                        acc[r.facility_id].scores[r.clause_code]=parseFloat(r.avg_score||r.total_score||0);
                        return acc;
                    },{}));
                    const PAGE_SIZE = 25;
                    const totalPages = Math.max(1, Math.ceil(allFacRows.length / PAGE_SIZE));
                    const safePage = Math.min(heatmapPage, totalPages - 1);
                    const pageRows = allFacRows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
                    const startN = allFacRows.length ? safePage * PAGE_SIZE + 1 : 0;
                    const endN   = Math.min(allFacRows.length, safePage * PAGE_SIZE + PAGE_SIZE);

                    return (
                        <XCard title="Clause Score Heatmap — by Facility" filename="Clause_Heatmap" style={{marginBottom:16}}
                            extra={
                                <span style={{fontSize:11,color:'#888',marginRight:8}}>
                                    {allFacRows.length>0
                                        ? `Showing ${startN}–${endN} of ${allFacRows.length} facilities`
                                        : 'No facilities'}
                                </span>
                            }>
                            <div style={{overflowX:'auto'}}>
                                <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                                    <thead>
                                        <tr style={{background:'#f0f5ff'}}>
                                            <th style={{padding:'6px 10px',textAlign:'left',borderBottom:'1px solid #dbeafe',minWidth:170}}>Facility</th>
                                            {heatmapClausesList.map(c => {
                                                const std = standards.find(s=>s.id===c.standard_id);
                                                return (
                                                    <th key={c.id} style={{padding:'6px 8px',textAlign:'center',borderBottom:'1px solid #dbeafe',color:std?.accent||'#1890ff',fontWeight:800,minWidth:52}}>
                                                        <div>{c.code}</div>
                                                        {!filters.standard_id && <div style={{fontSize:8,color:'#aaa',fontWeight:400}}>{std?.name?.slice(0,5)}</div>}
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageRows.map(([fid,fac],i)=>(
                                            <tr key={fid} style={{background:i%2?'#fafafe':'#fff'}}>
                                                <td style={{padding:'5px 10px',fontWeight:600,fontSize:11,maxWidth:170,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{fac.name}</td>
                                                {heatmapClausesList.map(c=>{
                                                    const v=fac.scores[c.code]; const r=v!=null?getRating(v):null;
                                                    return <td key={c.id} style={{padding:'3px 4px',textAlign:'center'}}>
                                                        {v!=null?<div style={{background:r.color,color:'#fff',borderRadius:4,padding:'2px 3px',fontWeight:700,fontSize:10}}>{v.toFixed(0)}%</div>:<span style={{color:'#ddd'}}>—</span>}
                                                    </td>;
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {totalPages > 1 && (
                                <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,marginTop:14}}>
                                    <Button size="small" disabled={safePage===0}
                                        onClick={()=>setHeatmapPage(p=>Math.max(0,p-1))}>
                                        ← Prev
                                    </Button>
                                    <span style={{fontSize:12,color:'#555',fontWeight:600}}>
                                        Page {safePage+1} of {totalPages}
                                    </span>
                                    <Button size="small" disabled={safePage>=totalPages-1}
                                        onClick={()=>setHeatmapPage(p=>Math.min(totalPages-1,p+1))}>
                                        Next →
                                    </Button>
                                </div>
                            )}
                        </XCard>
                    );
                })()}

                {/* Per-standard clause performance — one section per standard */}
                <ClausesByStandard
                    clauseData={clauseData}
                    standards={standards}
                    allClauses={allClauses}
                    selectedStdId={filters.standard_id}
                />
                </SectionTitle>{/* end clauses */}

                {/* ════ SECTION 5 — SUB-STANDARDS ════ */}
                {false && <><SectionTitle id="sec-substandards" icon={<ApartmentOutlined/>}
                    title="Clause Summary Cards"
                    subtitle="Per-clause performance grouped by standard — scores are never mixed across standards"
                    color="#10b981"/>
                {standards.filter(std => !filters.standard_id || std.id === filters.standard_id).map(std => {
                    const stdSubs = subData.filter(s => s.stdId === std.id);
                    if (!stdSubs.length) return null;
                    return (
                        <div key={std.id} style={{ marginBottom: 20 }}>
                            <div style={{ fontSize:12, fontWeight:700, color:std.accent||'#555', marginBottom:10,
                                padding:'6px 12px', background:`${std.accent||'#1890ff'}10`,
                                borderRadius:6, display:'inline-flex', alignItems:'center', gap:6 }}>
                                <span style={{ width:10, height:10, borderRadius:'50%', background:std.accent||'#1890ff', display:'inline-block' }}/>
                                {std.name}
                            </div>
                            <Row gutter={[14,14]} style={{ marginBottom:16 }}>
                                {stdSubs.map(s => {
                                    const r = getRating(s.avg);
                                    return (
                                        <Col key={s.code} span={6}>
                                            <div style={{border:`2px solid ${r.color}40`,borderRadius:12,padding:'16px 18px',background:`${r.color}07`,height:'100%'}}>
                                                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
                                                    <div>
                                                        <div style={{fontWeight:900,fontSize:14,color:std.accent||'#333'}}>{s.code}</div>
                                                        <div style={{fontSize:10,color:'#888',marginTop:2,lineHeight:1.5}}>{(s.title||'').slice(0,36)}</div>
                                                    </div>
                                                    <div style={{fontSize:24,fontWeight:900,color:r.color,lineHeight:1}}>{s.avg.toFixed(1)}%</div>
                                                </div>
                                                <Progress percent={Math.min(100,s.avg)} strokeColor={r.color} showInfo={false} size="small"/>
                                                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:6}}>
                                                    <Stars count={r.stars} size={12}/>
                                                    <span style={{fontSize:10,color:'#aaa'}}>{s.count} assessments</span>
                                                </div>
                                                <div style={{marginTop:4,fontSize:10,fontWeight:700,color:r.color}}>{r.label}</div>
                                            </div>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    );
                })}
                {subData.length===0 && <Empty description="No clause data for selected filters"/>}

                {/* ════ SECTION 6 — LOW MEs ════ */}
                </>
                }
                <SectionTitle id="sec-mes" icon={<TableOutlined/>} title="Low-Performing Clause Results" subtitle="Clause assessments scoring below 70% — requires immediate attention" color="#ef4444">
                {meData.length===0
                    ? <Alert message="No clause results below 70% for the selected filters." type="success" showIcon/>
                    : (
                        <XCard title={`${meData.length} clause results below 70%`} filename="Low_Performing_MEs">
                            <Table size="small" dataSource={meData} rowKey={(r,i)=>i} pagination={{pageSize:20,size:'small'}}
                                columns={[
                                    {title:'Facility',dataIndex:'facility_name',ellipsis:true,width:200},
                                    {title:'Clause',dataIndex:'clause_code',width:65,render:c=>{const cl=CLAUSES.find(x=>x.code===c);return <Tag style={{background:cl?.color||'#1890ff',color:'#fff',fontSize:10,border:'none'}}>{c}</Tag>;}},
                                    {title:'Date',dataIndex:'assessment_date',width:90,render:d=>d?moment(d).format('DD MMM YY'):'—'},
                                    {title:'✓ Met',dataIndex:'met_count',width:55,align:'center',render:v=><Tag color="green" style={{fontSize:10}}>{v}</Tag>},
                                    {title:'◑ Partial',dataIndex:'partially_met_count',width:65,align:'center',render:v=><Tag color="orange" style={{fontSize:10}}>{v}</Tag>},
                                    {title:'✗ Not Met',dataIndex:'not_met_count',width:70,align:'center',render:v=><Tag color="red" style={{fontSize:10}}>{v}</Tag>},
                                    {title:'Score',width:80,align:'center',render:(_,row)=>{const v=row.avg_score||row.total_score||0;const r=getRating(parseFloat(v||0));return <span style={{fontWeight:900,color:r.color}}>{parseFloat(v||0).toFixed(1)}%</span>;}},
                                    {title:'Rating',dataIndex:'interpretation',width:150,render:v=>{const r=RATING.find(x=>x.label===v)||RATING[5];return <Tag style={{background:r.color,color:'#fff',border:'none',fontSize:10}}>{v||'—'}</Tag>;}},
                                ]}/>
                        </XCard>
                    )}

                {/* ════ SECTION 7 — KPI vs ACTUALS ════ */}
                </SectionTitle>{/* end mes */}<SectionTitle id="sec-kpis" icon={<TrophyOutlined/>} title="KPI Targets vs Actuals" subtitle="Performance against defined targets by clause and assessment period. Note: KPI actuals are computed nationally and do not currently respect facility/region/level filters." color="#faad14">
                {(!kpiData.kpis||kpiData.kpis.length===0)
                    ? <Alert message="No KPI targets defined. Set targets in the KPI Targets module." type="info" showIcon/>
                    : (
                        <>
                            <Row gutter={[12,12]} style={{marginBottom:16}}>
                                {(kpiData.kpis||[]).filter(k=>k.kpi_type==='target').slice(0,6).map((kpi,i)=>{
                                    const acts=(kpiData.actuals||[]).filter(a=>(!kpi.clause_id||Number(a.clause_id)===Number(kpi.clause_id)));
                                    const avg=acts.length?acts.reduce((s,a)=>s+parseFloat(a.actual_pct||a.total_score||0),0)/acts.length:0;
                                    const met=avg>=parseFloat(kpi.target_pct||0);
                                    return (
                                        <Col key={i} span={4}>
                                            <Card size="small" bodyStyle={{padding:'10px 8px'}} style={{border:`2px solid ${met?'#52c41a':'#f5222d'}40`,background:met?'#f6ffed':'#fff2f0'}}>
                                                <div style={{fontSize:10,color:'#888',marginBottom:4,textAlign:'center'}}>{kpi.clause_code||'Overall'} • {kpi.period_label}</div>
                                                <ReactECharts option={gaugeOpt(kpi.target_pct,avg)} style={{height:110}}/>
                                                <div style={{textAlign:'center',fontSize:10,fontWeight:700,color:met?'#52c41a':'#f5222d'}}>{met?'✓ Target Met':'✗ Below Target'}</div>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                            <XCard title="KPI Target vs Actual — Full Table" filename="KPI_vs_Actual" style={{marginBottom:16}}>
                                <Table size="small" dataSource={(kpiData.kpis||[]).filter(k=>k.kpi_type==='target')} rowKey={(r,i)=>i} pagination={{pageSize:15,size:'small'}}
                                    columns={[
                                        {title:'Period',dataIndex:'period_label',width:100,render:p=><Tag color="blue">{p}</Tag>},
                                        {title:'Clause',dataIndex:'clause_code',width:80,render:c=>c?<Tag>{c}</Tag>:<Tag color="purple">Overall</Tag>},
                                        {title:'Level',dataIndex:'facility_level',width:100,render:l=>l||'All Levels'},
                                        {title:'Target',dataIndex:'target_pct',width:80,render:v=><strong style={{color:'#1890ff'}}>{v}%</strong>},
                                        {title:'Actual',width:80,render:(_,kpi)=>{const acts=(kpiData.actuals||[]).filter(a=>(!kpi.clause_id||Number(a.clause_id)===Number(kpi.clause_id)));const avg=acts.length?acts.reduce((s,a)=>s+parseFloat(a.actual_pct||a.total_score||0),0)/acts.length:null;return avg!=null?<span style={{fontWeight:700,color:avg>=kpi.target_pct?'#52c41a':'#f5222d'}}>{avg.toFixed(1)}%</span>:<span style={{color:'#ccc'}}>No data</span>;}},
                                        {title:'Gap',width:80,render:(_,kpi)=>{const acts=(kpiData.actuals||[]).filter(a=>(!kpi.clause_id||Number(a.clause_id)===Number(kpi.clause_id)));const avg=acts.length?acts.reduce((s,a)=>s+parseFloat(a.actual_pct||a.total_score||0),0)/acts.length:null;if(avg==null)return<span style={{color:'#ccc'}}>—</span>;const d=avg-parseFloat(kpi.target_pct);return<Tag color={d>=0?'success':'error'}>{d>=0?'+':''}{d.toFixed(1)}%</Tag>;}},
                                        {title:'Progress',width:130,render:(_,kpi)=>{const acts=(kpiData.actuals||[]).filter(a=>(!kpi.clause_id||Number(a.clause_id)===Number(kpi.clause_id)));const avg=acts.length?acts.reduce((s,a)=>s+parseFloat(a.actual_pct||a.total_score||0),0)/acts.length:0;const pct=parseFloat(kpi.target_pct)||1;return<Progress percent={Math.min(100,(avg/pct)*100)} size="small" strokeColor={avg>=pct?'#52c41a':'#f5222d'} showInfo={false}/>;}},
                                    ]}/>
                            </XCard>
                        </>
                    )}

                {/* ════ SECTION 8 — QUALITY INDEX ════ */}
                </SectionTitle>{/* end kpis */}<SectionTitle id="sec-qindex" icon={<StarFilled style={{color:'#faad14'}}/>} title="Quality Index" subtitle="Composite QoC assessment + customer survey weighted index per facility. Now recalculates server-side when clauses are filtered — the same mechanism the QoC report uses." color="#7c3aed">
                {qiData.length===0
                    ? <Alert message="No Quality Index data. Calculate from the Quality Index module." type="info" showIcon/>
                    : (
                        <>
                            <Row gutter={16} style={{marginBottom:16}}>
                                <Col span={16}>
                                    <XCard title="Quality Index — Facilities Ranked" filename="Quality_Index_Ranked">
                                        <ReactECharts option={qiBarOpt()} style={{height:Math.max(300,Math.min(qiData.length,qiPageSize)*24+60)}}/>
                                    </XCard>
                                </Col>
                                <Col span={8}>
                                    <XCard title="QI Rating Distribution" filename="QI_Rating_Distribution">
                                        <ReactECharts option={{
                                            tooltip:{trigger:'item',formatter:'{b}: {c} ({d}%)'},
                                            series:[{type:'pie',radius:['40%','70%'],center:['50%','50%'],
                                                data:RATING.map(r=>({name:r.label,value:qiFiltered.filter(q=>parseFloat(q.quality_index||0)>=r.min&&parseFloat(q.quality_index||0)<=r.max).length,itemStyle:{color:r.color}})).filter(d=>d.value>0),
                                                label:{formatter:'{b}\n{c}',fontSize:10}}]
                                        }} style={{height:300}}/>
                                    </XCard>
                                </Col>
                            </Row>
                            <XCard title="Quality Index — Full Table" filename="Quality_Index_Table" style={{marginBottom:16}}>
                                <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:8,flexWrap:'wrap'}}>
                                    <span style={{fontSize:12,color:'#888'}}>Show:</span>
                                    {[10,20,50,100].map(n=>(
                                        <button key={n} onClick={()=>setQiPageSize(n)}
                                            style={{padding:'2px 10px',borderRadius:6,border:'1px solid '+(qiPageSize===n?'#7c3aed':'#e5e7eb'),
                                                background:qiPageSize===n?'#7c3aed':'#fff',color:qiPageSize===n?'#fff':'#555',cursor:'pointer',fontSize:12}}>
                                            {n}
                                        </button>
                                    ))}
                                    <span style={{marginLeft:8,fontSize:12,color:'#888'}}>Order:</span>
                                    <button onClick={()=>setQiSortAsc(false)}
                                        style={{padding:'2px 10px',borderRadius:6,border:'1px solid '+(!qiSortAsc?'#7c3aed':'#e5e7eb'),
                                            background:!qiSortAsc?'#7c3aed':'#fff',color:!qiSortAsc?'#fff':'#555',cursor:'pointer',fontSize:12}}>
                                        High → Low
                                    </button>
                                    <button onClick={()=>setQiSortAsc(true)}
                                        style={{padding:'2px 10px',borderRadius:6,border:'1px solid '+(qiSortAsc?'#7c3aed':'#e5e7eb'),
                                            background:qiSortAsc?'#7c3aed':'#fff',color:qiSortAsc?'#fff':'#555',cursor:'pointer',fontSize:12}}>
                                        Low → High
                                    </button>
                                    <span style={{marginLeft:'auto',fontSize:12,color:'#888'}}>
                                        Showing top {qiPageSize} of {qiFiltered.length} facilities in chart above
                                    </span>
                                </div>
                                <Table size="small"
                                    dataSource={[...qiFiltered].sort((a,b)=>qiSortAsc?parseFloat(a.quality_index)-parseFloat(b.quality_index):parseFloat(b.quality_index)-parseFloat(a.quality_index))}
                                    rowKey="id"
                                    pagination={{pageSize:qiPageSize,showSizeChanger:true,pageSizeOptions:['10','20','50','100'],showTotal:(total,range)=>`${range[0]}-${range[1]} of ${total} facilities`,size:'small'}}
                                    columns={[
                                        {title:'Facility',dataIndex:'facility_name',ellipsis:true,width:200},
                                        {title:'Period',dataIndex:'period_label',width:100,render:p=><Tag color="blue">{p}</Tag>},
                                        {title:'QoC%',dataIndex:'qoc_score',width:75,align:'center',render:v=><span style={{fontWeight:700,color:'#1890ff'}}>{parseFloat(v||0).toFixed(1)}%</span>},
                                        {title:'Survey%',width:110,align:'center',render:(_,r)=>{const composite=parseFloat(r.survey_composite||0);const ipScore=parseFloat(r.inpatient_score||0);const opScore=parseFloat(r.outpatient_score||0);const total=parseInt(r.inpatient_count||0)+parseInt(r.outpatient_count||0);return(<div><span style={{fontWeight:700,color:total===0?'#f5222d':'#52c41a'}}>{composite.toFixed(1)}%</span>{total>0&&<div style={{fontSize:10,color:'#aaa',marginTop:2}}>IP:{ipScore.toFixed(0)} OP:{opScore.toFixed(0)} ({total})</div>}{total===0&&<div style={{fontSize:9,color:'#f5222d'}}>no surveys</div>}</div>);}},
                                        {title:'IP',dataIndex:'inpatient_count',width:45,align:'center'},
                                        {title:'OP',dataIndex:'outpatient_count',width:45,align:'center'},
                                        {title:'QI Score',dataIndex:'quality_index',width:85,align:'center',render:v=>{const r=getRating(parseFloat(v||0));return<span style={{fontWeight:900,color:r.color,fontSize:14}}>{parseFloat(v||0).toFixed(1)}%</span>;}},
                                        {title:'Stars',dataIndex:'star_rating',width:85,align:'center',render:v=><Stars count={v||0} size={12}/>},
                                        {title:'Rating',dataIndex:'interpretation',width:140,render:v=>{const r=RATING.find(x=>x.label===v)||RATING[5];return<Tag style={{background:r.color,color:'#fff',border:'none',fontSize:10}}>{v||'—'}</Tag>;}},
                                        {title:'Weights',width:100,align:'center',render:(_,r)=><span style={{fontSize:10,color:'#888'}}>{r.qoc_weight||80}/{r.survey_weight||20}%</span>},
                                    ]}/>
                            </XCard>
                        </>
                    )}

                {/* ════ SECTION 9 — SURVEY DASHBOARD ════ */}
                </SectionTitle>{/* end qindex */}<SectionTitle id="sec-surveys" icon="📝" title="Customer Satisfaction Survey Dashboard" subtitle="Inpatient and outpatient survey scores by facility and period" color="#3b82f6">
                {surveyData.surveys.length===0
                    ? <Alert message="No survey data found for the selected filters." type="info" showIcon/>
                    : (
                        <>
                            {(()=>{
                                const st=surveyData.stats||{};
                                const avg=parseFloat(st.average_score||0);
                                const satPct=(st.total_surveys||0)>0?Math.round((st.satisfied_count/st.total_surveys)*100):0;
                                return (
                                    <Row gutter={[12,12]} style={{marginBottom:16}}>
                                        {[
                                            {label:'Total Surveys',      value:st.total_surveys||0,   color:'#3b82f6',icon:'📝'},
                                            {label:'Inpatient',          value:st.inpatient_count||0, color:'#0050b3',icon:'🛏'},
                                            {label:'Outpatient',         value:st.outpatient_count||0,color:'#006837',icon:'🚶'},
                                            {label:'Avg Score',          value:`${avg.toFixed(1)}%`,  color:surveyScoreColor(avg),icon:'⭐'},
                                            {label:'Satisfied / Better', value:`${satPct}%`, sub:'≥70% score threshold', color:satPct>=70?'#52c41a':'#fa8c16',icon:'😊'},
                                        ].map(k=>(<Col key={k.label} span={k.sub?6:4}><KpiCard {...k}/></Col>))}
                                    </Row>
                                );
                            })()}
                            <Row gutter={16} style={{marginBottom:16}}>
                                <Col span={12}>
                                    <XCard title="IP vs OP Average Score by Facility" filename="Survey_IP_vs_OP">
                                        {Object.keys(surveyData.by_facility).length>0 ? <ReactECharts option={surveyBarOpt()} style={{height:Math.max(200,Object.keys(surveyData.by_facility).length*36+60)}}/> : <Empty description="No data"/>}
                                    </XCard>
                                </Col>
                                <Col span={6}>
                                    <XCard title="Inpatient Score Distribution" filename="Survey_IP_Distribution">
                                        {surveyData.surveys.some(s=>s.survey_type==='inpatient') ? <ReactECharts option={surveyDistOpt('inpatient')} style={{height:220}}/> : <Empty description="No inpatient surveys"/>}
                                    </XCard>
                                </Col>
                                <Col span={6}>
                                    <XCard title="Outpatient Score Distribution" filename="Survey_OP_Distribution">
                                        {surveyData.surveys.some(s=>s.survey_type==='outpatient') ? <ReactECharts option={surveyDistOpt('outpatient')} style={{height:220}}/> : <Empty description="No outpatient surveys"/>}
                                    </XCard>
                                </Col>
                            </Row>
                            {surveyData.surveys.length>1&&(
                                <XCard title="Survey Scores Over Time" filename="Survey_Timeline" style={{marginBottom:16}}>
                                    <ReactECharts option={surveyTimelineOpt()} style={{height:200}}/>
                                </XCard>
                            )}
                            <XCard title="Survey Coverage by Facility" filename="Survey_Coverage" style={{marginBottom:16}}>
                                <Table size="small" dataSource={Object.values(surveyData.by_facility)} rowKey="name" pagination={{pageSize:15,size:'small'}}
                                    columns={[
                                        {title:'Facility',dataIndex:'name',ellipsis:true,width:200},
                                        {title:'IP Surveys',width:90,align:'center',render:(_,r)=><Tag color="blue" style={{fontSize:10}}>{r.ip.length}</Tag>},
                                        {title:'IP Avg Score',width:110,align:'center',render:(_,r)=>{if(!r.ip.length)return<span style={{color:'#ccc'}}>—</span>;const avg=r.ip.reduce((s,x)=>s+parseFloat(x.overall_score||0),0)/r.ip.length;return<span style={{fontWeight:700,color:surveyScoreColor(avg)}}>{avg.toFixed(1)}%</span>;}},
                                        {title:'OP Surveys',width:90,align:'center',render:(_,r)=><Tag color="green" style={{fontSize:10}}>{r.op.length}</Tag>},
                                        {title:'OP Avg Score',width:110,align:'center',render:(_,r)=>{if(!r.op.length)return<span style={{color:'#ccc'}}>—</span>;const avg=r.op.reduce((s,x)=>s+parseFloat(x.overall_score||0),0)/r.op.length;return<span style={{fontWeight:700,color:surveyScoreColor(avg)}}>{avg.toFixed(1)}%</span>;}},
                                        {title:'Combined Avg',width:120,align:'center',render:(_,r)=>{const all=[...r.ip,...r.op];if(!all.length)return<span style={{color:'#ccc'}}>—</span>;const avg=all.reduce((s,x)=>s+parseFloat(x.overall_score||0),0)/all.length;return<span style={{fontWeight:900,fontSize:14,color:surveyScoreColor(avg)}}>{avg.toFixed(1)}%</span>;}},
                                        {title:'Latest Survey',width:110,render:(_,r)=>{const all=[...r.ip,...r.op].sort((a,b)=>moment(b.survey_date).unix()-moment(a.survey_date).unix());return all.length?<span style={{fontSize:11}}>{moment(all[0].survey_date).format('DD MMM YYYY')}</span>:'—';}},
                                    ]}/>
                            </XCard>
                            <XCard title="Recent Surveys" filename="Recent_Surveys" style={{marginTop:16}}>
                                <Table size="small"
                                    dataSource={[...surveyData.surveys].sort((a,b)=>moment(b.survey_date).unix()-moment(a.survey_date).unix()).slice(0,20)}
                                    rowKey="id" pagination={false}
                                    columns={[
                                        {title:'Facility',dataIndex:'facility_name',ellipsis:true,width:200},
                                        {title:'Type',dataIndex:'survey_type',width:100,render:t=><Tag color={t==='inpatient'?'blue':'green'} style={{textTransform:'capitalize',fontSize:10}}>{t}</Tag>},
                                        {title:'Date',dataIndex:'survey_date',width:110,render:d=>moment(d).format('DD MMM YYYY')},
                                        {title:'Score',dataIndex:'overall_score',width:130,render:s=>{const v=parseFloat(s||0);return<div><span style={{fontWeight:700,fontSize:14,color:surveyScoreColor(v)}}>{v.toFixed(1)}%</span><Progress percent={v} showInfo={false} size="small" strokeColor={surveyScoreColor(v)} style={{marginTop:2}}/></div>;}},
                                        {title:'Period',dataIndex:'period_label',width:100,render:p=>p?<Tag color="blue" style={{fontSize:10}}>{p}</Tag>:<span style={{color:'#ccc'}}>—</span>},
                                    ]}/>
                            </XCard>
                        </>
                    )}


                </SectionTitle>{/* end surveys */}{/* ════ SECTION 10 — PERIOD COMPARISON ════ */}
                {(!filters.region || filters.region.length===0) && <>
                <SectionTitle id="sec-comparison" icon="🔄" title="Period Comparison"
                    subtitle="Compare facility assessment scores across two audit periods side by side"
                    color="#0ea5e9">
                <PeriodComparison periods={periods.filter(p=>!p.label.startsWith('ENHEA'))} filters={filters} standards={standards} allClauses={allClauses} resolvedFacilityIds={resolvedFacilityIds} resolvedClauseCodes={resolvedClauseCodes}/>
                </SectionTitle>
                </>}

                </Spin>
            </div>
        </div>
        {reportFacility && (
            <FacilityReportModal facilityProp={reportFacility} onClose={()=>setReportFacility(null)}/>
        )}
        </>
    );
}